import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  TextField,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Stage, Layer, Image as KonvaImage, Rect, Circle, Group, Text, Path } from 'react-konva';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import boundingBoxApi from '../../../api/boundingBoxApi';
import { useTheme } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
// Import authenticated API service instead of raw axios
import { default as api } from '../../../services/api';
import uploadsApi from '../../../api/uploadsApi';

// Set base URL for API
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Annotation modes
const MODE = {
  VIEW: 'view',
  ANNOTATE: 'annotate',
};

// Focus modes for annotation visibility
const FOCUS_MODE = {
  OFF: 'off',           // Show all annotations (default)
  SELECTED: 'selected', // Show only selected annotation
};

// Status colors for display
const STATUS_COLORS = {
  'draft': '#FFC107', // amber
  'validated': '#4CAF50', // green
  'rejected': '#F44336', // red
  'new': '#2196F3' // blue - for defect status
};

// Color for defect type label
const DEFECT_TYPE_COLOR = '#3f51b5'; // primary blue

// Anchor points for resizing
const ANCHORS = [
  { x: 0, y: 0, cursor: 'nw-resize' },   // top-left
  { x: 0.5, y: 0, cursor: 'n-resize' },  // top-center
  { x: 1, y: 0, cursor: 'ne-resize' },   // top-right
  { x: 1, y: 0.5, cursor: 'e-resize' },  // right-center
  { x: 1, y: 1, cursor: 'se-resize' },   // bottom-right
  { x: 0.5, y: 1, cursor: 's-resize' },  // bottom-center
  { x: 0, y: 1, cursor: 'sw-resize' },   // bottom-left
  { x: 0, y: 0.5, cursor: 'w-resize' },  // left-center
];

// Label dimension constants for smart positioning
const LABEL_DIMENSIONS = {
  STATUS: { width: 115, height: 18, offsetY: 20, textOffsetX: 10, textOffsetY: 6 },
  DEFECT_TYPE: { height: 18, minWidth: 120, offsetY: 2, textOffsetX: 5, textOffsetY: 6 },
  SEVERITY: { height: 18, minWidth: 80, offsetY: 2, textOffsetX: 5, textOffsetY: 6 }
};

// Helper to calculate aspect-ratio-preserving display dimensions for an image within a stage.
// Uses uniform scaling so the image fits entirely without distortion.
// The Math.min(..., 1) ensures small images are not upscaled beyond their native size.
const getDisplayDimensions = (img, stage) => {
  if (!img || !stage.width || !stage.height || !img.width || !img.height) {
    return { width: 0, height: 0 };
  }
  const scale = Math.min(stage.width / img.width, stage.height / img.height, 1);
  return {
    width: img.width * scale,
    height: img.height * scale,
  };
};

// Helper to calculate smart label positions based on image bounds
const calculateLabelPositions = (annotation, imageBounds) => {
  const { y, height } = annotation;
  const { top: imageTop, bottom: imageBottom } = imageBounds;

  const statusH = LABEL_DIMENSIONS.STATUS.height;
  const defectH = LABEL_DIMENSIONS.DEFECT_TYPE.height;
  const severityH = LABEL_DIMENSIONS.SEVERITY.height;
  const gap = 2;

  // Default positions
  const statusAboveY = y - LABEL_DIMENSIONS.STATUS.offsetY;
  const defectBelowY = y + height + LABEL_DIMENSIONS.DEFECT_TYPE.offsetY;
  const severityBelowY = defectBelowY + defectH + gap; // Severity stacks below defect type

  // Check overflows
  const statusOverflowsTop = statusAboveY < imageTop;
  const defectOverflowsBottom = (defectBelowY + defectH) > imageBottom;
  const severityOverflowsBottom = (severityBelowY + severityH) > imageBottom;

  // Case 1: All fit in default positions
  if (!statusOverflowsTop && !defectOverflowsBottom && !severityOverflowsBottom) {
    return {
      statusY: statusAboveY,
      defectY: defectBelowY,
      severityY: severityBelowY
    };
  }

  // Case 2: Status overflows top → stack all below
  if (statusOverflowsTop) {
    return {
      statusY: y + height + gap,
      defectY: y + height + gap + statusH + gap,
      severityY: y + height + gap + statusH + gap + defectH + gap
    };
  }

  // Case 3: Bottom labels overflow → stack all above
  if (defectOverflowsBottom || severityOverflowsBottom) {
    return {
      severityY: y - severityH - gap,
      defectY: y - severityH - gap - defectH - gap,
      statusY: y - severityH - gap - defectH - gap - statusH - gap
    };
  }

  // Fallback: default positions
  return {
    statusY: statusAboveY,
    defectY: defectBelowY,
    severityY: severityBelowY
  };
};

// Box style configuration
const BOX_STYLE = {
  // STATUS-BASED STYLES (overrides mode-based styles)
  STATUS: {
    DRAFT: {
      stroke: '#FFC107', // amber
      strokeWidth: 2,
      dash: [5, 5], // Always dashed for draft
    },
    VALIDATED: {
      stroke: '#4CAF50', // green
      strokeWidth: 2,
      dash: null, // Always solid for validated
    },
    REJECTED: {
      stroke: '#F44336', // red  
      strokeWidth: 2,
      dash: [2, 2], // Short dashes for rejected
    }
  },
  // ANNOTATION MODE STYLES (used when status style doesn't apply)
  ANNOTATE: {
    NORMAL: {
      stroke: 'yellow',
      strokeWidth: 2,
      dash: [5, 5], // Creates dotted line effect
    },
    SELECTED: {
      stroke: 'orange',
      strokeWidth: 3,
      dash: [5, 5],
    },
    DRAWING: {
      stroke: 'yellow',
      strokeWidth: 2,
      dash: [5, 5],
    }
  },
  // VIEW MODE STYLES (used when status style doesn't apply)
  VIEW: {
    NORMAL: {
      stroke: 'blue',
      strokeWidth: 2,
      dash: null, // Solid line
    }
  }
};

// Helper to convert an array of points to an SVG path string for Konva
const pointsToPath = (points) => {
  if (!points || points.length === 0) return '';
  const path = points.map((p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    return `L ${p.x} ${p.y}`;
  }).join(' ');
  return path + ' Z'; // Z closes the path
};

const Enhanced_ImageViewerModal = ({
  open,
  onClose,
  photo,
  onDataMutated,
  viewOnlyMode = false,
  // New navigation props
  photos = [],
  currentIndex = 0,
  onNavigate = null,
  // Fullscreen mode prop - renders inline instead of Dialog
  fullScreenMode = false,
  // Pre-resolved context from parent (avoids redundant API calls)
  parentContext = null
}) => {
  const [image, setImage] = useState(null);
  const [mode, setMode] = useState(MODE.VIEW);
  const [showSegmentation, setShowSegmentation] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [activeAnchorIndex, setActiveAnchorIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState(null);
  
  // Add zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [isZoomed, setIsZoomed] = useState(false);
  const isZoomedRef = useRef(false);
  
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const isAnchorDraggingRef = useRef(false);
  const imageObjRef = useRef(null);
  const imageStateRef = useRef(null);
  const zoomPendingRef = useRef(null);
  const zoomRetryRef = useRef(0);
  const zoomTimeoutRef = useRef(null);
  const handleZoomToAnnotationRef = useRef(null);
  // Refs to avoid stale closures in keyboard handlers
  const annotationsRef = useRef([]);
  const selectedAnnotationRef = useRef(null);
  const photosRef = useRef([]);
  const currentIndexRef = useRef(0);
  const onNavigateRef = useRef(null);

  // Add state for API-related feedback
  const [apiStatus, setApiStatus] = useState({ open: false, severity: 'info', message: '' });
  
  // Add state for status menu
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedStatusAnnotation, setSelectedStatusAnnotation] = useState(null);
  
  // Available status options for bounding boxes
  const statusOptions = ['draft', 'validated', 'rejected'];

  // Add state for defect type menu
  const [defectTypeMenuAnchor, setDefectTypeMenuAnchor] = useState(null);
  const [selectedDefectTypeAnnotation, setSelectedDefectTypeAnnotation] = useState(null);
  const [defectTypes, setDefectTypes] = useState([]);

  // Add state for confirmation dialog
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);

  // Add state for severity level menu
  const [severityLevels, setSeverityLevels] = useState([]);
  const [severityMenuAnchor, setSeverityMenuAnchor] = useState(null);
  const [selectedSeverityAnnotation, setSelectedSeverityAnnotation] = useState(null);

  // Add state for notes dialog during status changes
  const [statusNotesDialogOpen, setStatusNotesDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { annotation, newStatus }
  const [statusRemarks, setStatusRemarks] = useState('');

  // Add state for notes editing dialog
  const [notesEditDialogOpen, setNotesEditDialogOpen] = useState(false);
  const [editingNotesAnnotation, setEditingNotesAnnotation] = useState(null);
  const [editingNotes, setEditingNotes] = useState('');

  // Add state for focus mode
  const [focusMode, setFocusMode] = useState(FOCUS_MODE.OFF);
  const focusModeRef = useRef(FOCUS_MODE.OFF);

  // Add state for severity legend popover
  const [legendAnchorEl, setLegendAnchorEl] = useState(null);

  // Add state for segmentation color randomization
  const [useRandomColors, setUseRandomColors] = useState(false);
  const [segmentationColors, setSegmentationColors] = useState(new Map());

  // Function to generate a random bright color
  const generateRandomColor = () => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', 
      '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA', '#F1948A', '#85C1E9',
      '#FF8A65', '#81C784', '#64B5F6', '#FFB74D', '#A1887F', '#90A4AE', '#E57373',
      '#9575CD', '#4FC3F7', '#81C784', '#FFF176', '#FF8A65', '#A1887F', '#90A4AE'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Function to randomize segmentation colors per detection
  const randomizeSegmentationColors = () => {
    const newColorMap = new Map();
    
    // Get unique detection IDs from current annotations
    const uniqueDetectionIds = [...new Set(annotations.map(ann => ann.id))];
    
    // Assign a unique color to each detection
    uniqueDetectionIds.forEach(detectionId => {
      newColorMap.set(detectionId, generateRandomColor());
    });
    
    setSegmentationColors(newColorMap);
    setUseRandomColors(true);
    
    console.log(`[Enhanced_ImageViewerModal] Assigned random colors to ${uniqueDetectionIds.length} detections:`, 
      Object.fromEntries(newColorMap));
  };

  // Function to reset to default colors
  const resetSegmentationColors = () => {
    setUseRandomColors(false);
    setSegmentationColors(new Map());
  };

  // Auto-assign colors when annotations change (if random colors are enabled)
  useEffect(() => {
    if (useRandomColors && annotations.length > 0) {
      const newColorMap = new Map(segmentationColors);
      let hasNewDetections = false;
      
      // Check for any new detections that don't have colors assigned yet
      annotations.forEach(annotation => {
        if (!newColorMap.has(annotation.id)) {
          newColorMap.set(annotation.id, generateRandomColor());
          hasNewDetections = true;
        }
      });
      
      if (hasNewDetections) {
        setSegmentationColors(newColorMap);
        console.log(`[Enhanced_ImageViewerModal] Auto-assigned colors to new detections`);
      }
    }
  }, [annotations, useRandomColors, segmentationColors]);

  // Keep refs in sync with latest state
  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  useEffect(() => {
    selectedAnnotationRef.current = selectedAnnotation;
  }, [selectedAnnotation]);

  useEffect(() => {
    focusModeRef.current = focusMode;
  }, [focusMode]);

  useEffect(() => {
    isZoomedRef.current = isZoomed;
  }, [isZoomed]);

  useEffect(() => {
    imageStateRef.current = image;
    // If a zoom was pending because stage/image wasn't ready, try now
    if (image && zoomPendingRef.current && stageSize.width > 0 && stageSize.height > 0) {
      const annotation = zoomPendingRef.current;
      zoomPendingRef.current = null;
      zoomRetryRef.current = 0;
      console.log('Retrying pending zoom after image ready');
      // small timeout to ensure Konva has rendered
      clearTimeout(zoomTimeoutRef.current);
      zoomTimeoutRef.current = setTimeout(() => {
        handleZoomToAnnotation(annotation);
      }, 0);
    }
  }, [image]);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);

  // Navigation functions
  const canGoNext = photos.length > 0 && currentIndex < photos.length - 1;
  const canGoPrevious = photos.length > 0 && currentIndex > 0;

  // Focus mode functions
  const toggleFocusMode = () => {
    if (focusMode === FOCUS_MODE.OFF) {
      if (selectedAnnotation) {
        setFocusMode(FOCUS_MODE.SELECTED);
      } else {
        // If no annotation is selected, show a message
        setApiStatus({
          open: true,
          severity: 'info',
          message: 'Please select an annotation to enable focus mode.'
        });
      }
    } else {
      setFocusMode(FOCUS_MODE.OFF);
    }
  };

  // Focus on specific annotation (for table actions)
  const focusOnAnnotation = (annotation) => {
    setSelectedAnnotation(annotation);
    setFocusMode(FOCUS_MODE.SELECTED);
    
    // Optional: Auto-zoom to the annotation
    if (annotation) {
      handleZoomToAnnotation(annotation);
    }
  };

  // Cycle through annotations in focus mode
  const cycleFocusToNextAnnotation = () => {
    const list = annotationsRef.current || [];
    if (list.length === 0) return;

    const current = selectedAnnotationRef.current;
    let currentIndex = -1;
    if (current) {
      currentIndex = list.findIndex(ann => ann.id === current.id);
    }

    const nextIndex = (currentIndex + 1) % list.length;
    const nextAnnotation = list[nextIndex];

    setSelectedAnnotation(nextAnnotation);

    // Auto-activate focus mode if not already active
    if (focusModeRef.current === FOCUS_MODE.OFF) {
      setFocusMode(FOCUS_MODE.SELECTED);
    }

    // Do NOT auto-zoom on Tab; Space toggles zoom explicitly
    console.log('Tab cycling: Set annotation and focus mode', {
      nextAnnotation: nextAnnotation.id,
      focusMode: FOCUS_MODE.SELECTED
    });

    // Show feedback
    setApiStatus({
      open: true,
      severity: 'info',
      message: `Focused on annotation ${nextIndex + 1} of ${list.length}`
    });
  };

  // Get visible annotations based on focus mode
  const getVisibleAnnotations = () => {
    if (focusMode === FOCUS_MODE.OFF) {
      return annotations; // Show all
    }
    
    if (focusMode === FOCUS_MODE.SELECTED && selectedAnnotation) {
      return annotations.filter(ann => ann.id === selectedAnnotation.id);
    }
    
    return annotations; // Fallback to show all
  };

  const handlePrevious = () => {
    if (canGoPrevious && onNavigate) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext && onNavigate) {
      onNavigate(currentIndex + 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open) return;
      // Ignore key handling when typing in inputs/textareas or contentEditable
      const target = event.target;
      const tag = target && target.tagName ? target.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          // Use refs to avoid stale values
          if ((photosRef.current || []).length > 0 && currentIndexRef.current > 0 && onNavigateRef.current) {
            // Reset focus mode before navigating to previous image
            setFocusMode(FOCUS_MODE.OFF);
            setSelectedAnnotation(null);
            onNavigateRef.current(currentIndexRef.current - 1);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          // Use refs to avoid stale values
          if ((photosRef.current || []).length > 0 && currentIndexRef.current < photosRef.current.length - 1 && onNavigateRef.current) {
            // Reset focus mode before navigating to next image
            setFocusMode(FOCUS_MODE.OFF);
            setSelectedAnnotation(null);
            onNavigateRef.current(currentIndexRef.current + 1);
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (focusMode !== FOCUS_MODE.OFF) {
            setFocusMode(FOCUS_MODE.OFF);
          } else {
            handleSafeClose();
          }
          break;
        case 'f':
        case 'F':
          event.preventDefault();
          toggleFocusMode();
          break;
        case 'Tab':
          event.preventDefault();
          // Cycle through annotations using refs to avoid stale closures
          if ((annotationsRef.current || []).length > 0) {
            cycleFocusToNextAnnotation();
          }
          break;
        case ' ': // Space key
          event.preventDefault(); // Always prevent default for space to avoid page scroll
          console.log('Space key pressed!', {
            focusMode: focusModeRef.current,
            selectedAnnotation: selectedAnnotationRef.current,
            isZoomed: isZoomedRef.current
          });
          
          // Spacebar toggles zoom in focus mode on selected annotation
          if (focusModeRef.current === FOCUS_MODE.SELECTED && selectedAnnotationRef.current) {
            console.log('Space: Toggling zoom for selected annotation');
            if (isZoomedRef.current) {
              console.log('Space: Resetting zoom');
              resetZoom();
            } else {
              console.log('Space: Zooming to annotation');
              // Use ref to avoid stale closure
              if (handleZoomToAnnotationRef.current) {
                handleZoomToAnnotationRef.current(selectedAnnotationRef.current);
              }
            }
          } else {
            console.log('Space: Not in focus mode or no selected annotation');
          }
          break;
        default:
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open]);

  // Get box style based on current mode and selection state
  const getBoxStyle = (isSelected, status) => {
    // First priority: Status-based styling (for draft, validated, rejected)
    if (status === 'draft') {
      return BOX_STYLE.STATUS.DRAFT;
    } else if (status === 'validated') {
      return BOX_STYLE.STATUS.VALIDATED;
    } else if (status === 'rejected') {
      return BOX_STYLE.STATUS.REJECTED;
    }
    
    // Second priority: Mode-based styling
    if (mode === MODE.ANNOTATE) {
      return isSelected ? BOX_STYLE.ANNOTATE.SELECTED : BOX_STYLE.ANNOTATE.NORMAL;
    } else {
      return BOX_STYLE.VIEW.NORMAL;
    }
  };

  // Reset image state when modal is opened/closed or photo changes
  useEffect(() => {
    if (!open) {
      // Clear image state when modal is closed
      setImage(null);
      // Also clear annotations to prevent any errors when component unmounts
      setAnnotations([]);
      setSelectedAnnotation(null);
      setCurrentAnnotation(null);
      setLoadError(null); // Also reset error
      setIsLoading(false); // Reset loading
      setFocusMode(FOCUS_MODE.OFF); // Reset focus mode
      return;
    }
    
    // Always reset focus mode when new photo is loaded
    setFocusMode(FOCUS_MODE.OFF);
    setSelectedAnnotation(null);
    
    // Reset random colors when photo changes
    resetSegmentationColors();

    // Load the image when the modal is opened and photo exists
    if (open && photo) {
      loadImage();
    }
  }, [open, photo]);

  // Function to load the image using a presigned URL (with cache)
  const loadImage = async () => {
    if (!photo || !photo.url) {
      console.error("No photo or photo URL provided");
      setLoadError("No photo provided");
      setIsLoading(false);
      return;
    }

    console.log("Attempting to load image via presigned URL for key:", photo.url);
    setIsLoading(true);
    setLoadError(null);
    setAnnotations([]);
    setImage(null);

    try {
      // Get presigned URL (returns from cache if thumbnail already fetched it)
      const presignedUrl = await uploadsApi.getSecureUrl(photo.url);

      if (!presignedUrl) {
        throw new Error("Presigned URL was not returned from the server.");
      }

      // Load image directly via browser (enables progressive rendering, no blob overhead)
      const img = new window.Image();
      imageObjRef.current = img;
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        console.log("Image loaded successfully from presigned URL:", img.width, img.height);
        setImage(img);
        setIsLoading(false);
      };

      img.onerror = (e) => {
        console.error("Error loading image from presigned URL:", e);
        setLoadError("Failed to display fetched image. Please try again.");
        setIsLoading(false);
        setImage(null);
      };

      img.src = presignedUrl;

    } catch (error) {
      console.error("Error fetching image via presigned URL:", error);
      let errorMessage = "Failed to load image.";
      if (error.response) {
        errorMessage += ` Status: ${error.response.status}`;
      } else if (error.request) {
        errorMessage += " The request was made but no response was received.";
      } else {
        errorMessage += ` ${error.message}`;
      }
      setLoadError(errorMessage);
      setIsLoading(false);
      setImage(null);
    }
  };

  // Unified effect: load defect types + severity levels (resolves asset context once)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchContextData = async () => {
      if (!photo) {
        console.warn('[Enhanced_ImageViewerModal] No photo data, cannot determine defect type filters. Using generic fallback.');
        setDefectTypes([
          { id: 1, name: 'spalling' },
          { id: 2, name: 'crack' },
          { id: 3, name: 'vegetation' },
          { id: 4, name: 'joint failure' },
          { id: 5, name: 'rust staining' },
          { id: 6, name: 'drainage defect' }
        ]);
        setSeverityLevels([]);
        return;
      }

      let assetTypeId = null;
      let organizationId = null;

      try {
        // Step 1: Resolve asset context — prefer parentContext, fallback to API
        if (parentContext?.assetDetails) {
          assetTypeId = parentContext.assetDetails.general_type_id;
          organizationId = parentContext.assetDetails.organization_id;
          console.log(`[Enhanced_ImageViewerModal] Using parentContext: assetTypeId=${assetTypeId}, organizationId=${organizationId}`);
        } else {
          // Fallback: fetch from API (handles map-click path and other cases)
          const inspectionId = photo.inspection_id || photo.inspectionId;
          if (inspectionId) {
            console.log(`[Enhanced_ImageViewerModal] Fetching inspection details for ID: ${inspectionId}`);
            const inspectionResponse = await api.get(`/inspections/${inspectionId}`);
            const inspectionData = inspectionResponse.data;
            const assetIdFromInspection = inspectionData?.asset_id;
            const orgIdFromInspection = inspectionData?.organization_id;

            if (assetIdFromInspection) {
              console.log(`[Enhanced_ImageViewerModal] Fetching asset details for ID: ${assetIdFromInspection}`);
              const assetResponse = await api.get(`/assets/${assetIdFromInspection}`);
              const assetData = assetResponse.data;
              assetTypeId = assetData?.general_type_id;
              organizationId = assetData?.organization_id;
            } else if (orgIdFromInspection) {
              organizationId = orgIdFromInspection;
            }
          } else if (photo.asset_id) {
            const assetResponse = await api.get(`/assets/${photo.asset_id}`);
            const assetData = assetResponse.data;
            assetTypeId = assetData?.general_type_id;
            organizationId = assetData?.organization_id;
          } else if (photo.organization_id) {
            organizationId = photo.organization_id;
          }
        }

        if (cancelled) return;

        // Step 2: Fetch defect types and severity levels in PARALLEL
        const defectTypesPromise = (parentContext?.defectTypes?.length > 0)
          ? Promise.resolve(parentContext.defectTypes)
          : (async () => {
              const params = {};
              if (assetTypeId) params.asset_type_id = assetTypeId;
              if (organizationId) params.organization_id = organizationId;
              console.log('[Enhanced_ImageViewerModal] Fetching defect types with params:', params);
              const response = await api.get(`/defect-types`, { params });
              return (Array.isArray(response.data) && response.data.length > 0) ? response.data : [];
            })();

        const severityPromise = (organizationId && assetTypeId)
          ? api.get(`/severity-scheme-for-asset`, {
              params: { organization_id: organizationId, asset_type_id: assetTypeId }
            }).then(r => r.data?.levels || []).catch(err => {
              console.error('[Enhanced_ImageViewerModal] Error loading severity levels:', err);
              return [];
            })
          : Promise.resolve([]);

        const [defectTypesData, severityData] = await Promise.all([defectTypesPromise, severityPromise]);

        if (cancelled) return;

        console.log(`[Enhanced_ImageViewerModal] Loaded ${defectTypesData.length} defect types, ${severityData.length} severity levels`);
        setDefectTypes(defectTypesData);
        setSeverityLevels(severityData);

      } catch (error) {
        console.error('[Enhanced_ImageViewerModal] Error loading context data:', error);
        if (!cancelled) {
          setApiStatus({
            open: true,
            severity: 'error',
            message: `API Error: Could not load defect types. ${error.message || 'Unknown error'}`
          });
          setDefectTypes([]);
          setSeverityLevels([]);
        }
      }
    };

    fetchContextData();
    return () => { cancelled = true; };
  }, [open, photo, parentContext]);

  // NEW useEffect to load annotations when image and stageSize are ready
  useEffect(() => {
    if (open && photo && photo.id && image && stageSize.width > 0 && stageSize.height > 0) {
      // 'image' here is the HTMLImageElement from the state, passed as 'loadedImageElement'
      loadExistingAnnotations(photo.id, image);
    }
  }, [open, photo, image, stageSize]);

  // Function to load existing annotations for a photo
  const loadExistingAnnotations = async (photoId, loadedImageElement) => {
    console.log('[Enhanced_ImageViewerModal] Attempting to load existing annotations for photo ID:', photoId, 'With image element:', loadedImageElement);
    if (!photoId) {
      console.error('[Enhanced_ImageViewerModal] Cannot load annotations: photoId is undefined.');
      setApiStatus({
        open: true,
        severity: 'error',
        message: 'Error: Cannot load annotations due to missing photo ID.'
      });
      return;
    }
    
    try {
      setApiStatus({
        open: true,
        severity: 'info',
        message: 'Loading annotations...'
      });
      
      console.log('[Enhanced_ImageViewerModal] Calling API to get bounding boxes for photo ID:', photoId);
      const existingBoxes = await boundingBoxApi.getAll({ photo_id: photoId });
      console.log('[Enhanced_ImageViewerModal] Received existing boxes from API:', existingBoxes);
      
      if (existingBoxes && existingBoxes.length > 0) {
        // Use the passed loadedImageElement for calculations
        if (!loadedImageElement) {
          console.warn('[Enhanced_ImageViewerModal] Image element not provided to loadExistingAnnotations. Photo object:', photo);
          setApiStatus({
            open: true,
            severity: 'warning',
            message: 'Image data missing, annotations might not display correctly. Try reopening.'
          });
          return; 
        }
        
        // Get the image display dimensions (aspect-ratio-preserving) and original dimensions
        const { width: displayWidth, height: displayHeight } = getDisplayDimensions(loadedImageElement, stageSize);
        const originalWidth = photo.width || loadedImageElement.naturalWidth || displayWidth;
        const originalHeight = photo.height || loadedImageElement.naturalHeight || displayHeight;
        
        // Calculate the display-to-original scale factor (how much smaller the display is)
        const scaleFactorX = displayWidth / originalWidth;
        const scaleFactorY = displayHeight / originalHeight;
        
        // Get the image position offset in the stage
        const imagePositionX = (stageSize.width - displayWidth) / 2;
        const imagePositionY = (stageSize.height - displayHeight) / 2;
        
        console.log('[Enhanced_ImageViewerModal] Loading annotations with direct image element and scaling factors:', {
          displayWidth, displayHeight, originalWidth, originalHeight,
          scaleFactorX, scaleFactorY, imagePositionX, imagePositionY,
          loadedImageElementWidth: loadedImageElement.width,
          loadedImageElementHeight: loadedImageElement.height
        });
        
        // Transform bounding boxes from database format to annotation format
        const loadedAnnotations = existingBoxes.map(box => {
          const originalBoxWidth = box.x2 - box.x1;
          const originalBoxHeight = box.y2 - box.y1;
          
          const displayX = (box.x1 * scaleFactorX) + imagePositionX;
          const displayY = (box.y1 * scaleFactorY) + imagePositionY;
          const displayAnnotationWidth = originalBoxWidth * scaleFactorX;
          const displayAnnotationHeight = originalBoxHeight * scaleFactorY;

        // Process segmentation polygon(s) - handle both single and multiple polygons
        let scaledPolygonPoints = null;
        if (box.segmentation_polygon && Array.isArray(box.segmentation_polygon) && box.segmentation_polygon.length > 0) {
          // Check if this is multiple polygons (array of arrays) or single polygon (array of points)
          const isMultiplePolygons = Array.isArray(box.segmentation_polygon[0]) && Array.isArray(box.segmentation_polygon[0][0]);
          
          console.log(`[Enhanced_ImageViewerModal] Processing segmentation for box ${box.id}:`, {
            isMultiplePolygons,
            polygonCount: isMultiplePolygons ? box.segmentation_polygon.length : 1,
            firstPolygonPointCount: isMultiplePolygons ? box.segmentation_polygon[0]?.length : box.segmentation_polygon.length,
            rawData: box.segmentation_polygon
          });
          
          if (isMultiplePolygons) {
            // Multiple polygons: array of polygon arrays
            scaledPolygonPoints = box.segmentation_polygon.map(polygon => 
              polygon.map(point => ({
                x: (point[0] * scaleFactorX) + imagePositionX,
                y: (point[1] * scaleFactorY) + imagePositionY,
              }))
            );
            console.log(`[Enhanced_ImageViewerModal] Processed ${scaledPolygonPoints.length} polygons for box ${box.id}`);
          } else {
            // Single polygon: array of points (backward compatibility)
            scaledPolygonPoints = [box.segmentation_polygon.map(point => ({
              x: (point[0] * scaleFactorX) + imagePositionX,
              y: (point[1] * scaleFactorY) + imagePositionY,
            }))];
            console.log(`[Enhanced_ImageViewerModal] Processed single polygon (backward compatibility) for box ${box.id}`);
          }
        }
          
          return {
            id: box.id.toString(),
            x: displayX,
            y: displayY,
            width: displayAnnotationWidth,
            height: displayAnnotationHeight,
            segmentationPolygon: scaledPolygonPoints,
            defectTypeId: box.defect_type_id,
            defectType: box.defect_type_name,
            status: box.status,
            severityLevelId: box.severity_level_id,
            severityLevel: box.severity_level_name,
            isAiGenerated: box.is_ai_generated,
            confidenceScore: box.confidence_score,
            notes: box.notes,
            originalX1: box.x1,
            originalY1: box.y1,
            originalX2: box.x2,
            originalY2: box.y2,
            originalDefectTypeId: box.defect_type_id,
            originalSeverityLevelId: box.severity_level_id,
            defect_id: box.defect_id, // Add defect ID for validated annotations
            has_defect: box.has_defect, // Add defect flag
            updated_by: box.updated_by,
            created_by_full_name: box.created_by_full_name || null,
            created_by_email: box.created_by_email || null,
            updated_by_full_name: box.updated_by_full_name || null,
            updated_by_email: box.updated_by_email || null,
            updated_at: box.updated_at || null
          };
        });
        
        setAnnotations(loadedAnnotations);
        setApiStatus({
          open: true,
          severity: 'success',
          message: `Loaded ${loadedAnnotations.length} existing annotation(s)`
        });
      } else {
        setApiStatus({
          open: false
        });
      }
    } catch (error) {
      console.error('Error loading existing annotations:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error loading annotations: ${error.message || 'Unknown error'}`
      });
    }
  };

  // Adjust stage size based on container size
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateSize = () => {
      const container = containerRef.current;
      if (container) {
        const newSize = {
          width: container.offsetWidth,
          height: container.offsetHeight,
        };
        console.log("Updating stage size:", newSize);
        setStageSize(newSize);
        if (newSize.width > 0 && newSize.height > 0 && zoomPendingRef.current && imageStateRef.current) {
          const annotation = zoomPendingRef.current;
          zoomPendingRef.current = null;
          zoomRetryRef.current = 0;
          console.log('Retrying pending zoom after size ready');
          clearTimeout(zoomTimeoutRef.current);
          zoomTimeoutRef.current = setTimeout(() => {
            handleZoomToAnnotation(annotation);
          }, 0);
        }
      }
    };
    
    updateSize();
    
    // Add resize listener
    window.addEventListener('resize', updateSize);
    
    // Force a size update after a short delay to handle any initial sizing issues
    const timeoutId = setTimeout(() => {
      updateSize();
    }, 300);
    
    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timeoutId);
    };
  }, [open, image]);

  // Reset annotation state when mode changes
  useEffect(() => {
    if (mode === MODE.VIEW) {
      setSelectedAnnotation(null);
      setActiveAnchorIndex(null);
      setCurrentAnnotation(null);
      setIsDrawing(false);
    }
  }, [mode]);

  // If viewOnlyMode is true, ensure mode is always VIEW
  useEffect(() => {
    if (viewOnlyMode) {
      setMode(MODE.VIEW);
    }
  }, [viewOnlyMode, open]);

  const toggleAnnotationMode = () => {
    if (viewOnlyMode) return; // Prevent mode change if viewOnlyMode is true
    setMode(mode === MODE.VIEW ? MODE.ANNOTATE : MODE.VIEW);
  };

  const handleStageMouseDown = (e) => {
    // Only allow interaction in ANNOTATE mode
    if (mode !== MODE.ANNOTATE) return;
    
    // Get the clicked target
    const clickedOnStage = e.target === e.target.getStage();
    const clickedOnImage = e.target === imageRef.current;
    
    // If clicked on an anchor point, we'll handle it in the anchor's event handler
    if (isAnchorDraggingRef.current) return;
    
    // If clicked on stage background or the image, start drawing a new box
    if (clickedOnStage || clickedOnImage) {
      setSelectedAnnotation(null);
      setActiveAnchorIndex(null);
      
      const stage = stageRef.current;
      const point = stage.getPointerPosition();
      
      if (!point) return;
      
      const { x, y } = point;
      
      // Start drawing new rectangle
      setCurrentAnnotation({
        x,
        y,
        width: 0,
        height: 0,
        id: Date.now().toString(),
      });
      
      setIsDrawing(true);
      console.log('Starting to draw new annotation at', x, y);
    }
  };

  const handleStageMouseMove = (e) => {
    const stage = stageRef.current;
    if (!stage) return;
    
    // Only allow interaction in ANNOTATE mode
    if (mode !== MODE.ANNOTATE) {
      stage.container().style.cursor = 'default';
      return;
    }
    
    // Handle dragging an existing annotation
    if (isDragging && selectedAnnotation && !activeAnchorIndex) {
      const point = stage.getPointerPosition();
      if (!point || !dragStartPosition) return;
      
      // Calculate the movement delta
      const dx = point.x - dragStartPosition.x;
      const dy = point.y - dragStartPosition.y;
      
      // Update the selected annotation's position
      const newX = selectedAnnotation.startX + dx;
      const newY = selectedAnnotation.startY + dy;
      
      // Update annotations array
      const updatedAnnotations = annotations.map((ann) => {
        if (ann.id === selectedAnnotation.id) {
          return { ...ann, x: newX, y: newY };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      setSelectedAnnotation({ ...selectedAnnotation, x: newX, y: newY });
      
      // Set cursor to move
      stage.container().style.cursor = 'move';
      return;
    }
    
    // Handle resizing existing annotation
    if (activeAnchorIndex !== null && selectedAnnotation) {
      const point = stage.getPointerPosition();
      if (!point) return;
      
      const rect = { ...selectedAnnotation };
      const anchor = ANCHORS[activeAnchorIndex];
      
      // Calculate new position and dimensions based on which anchor was dragged
      let newProps = {};
      
      // Handle different resize anchor points
      switch (activeAnchorIndex) {
        case 0: // top-left
          newProps = {
            x: point.x,
            y: point.y,
            width: rect.x + rect.width - point.x,
            height: rect.y + rect.height - point.y
          };
          break;
        case 1: // top-center
          newProps = {
            y: point.y,
            height: rect.y + rect.height - point.y
          };
          break;
        case 2: // top-right
          newProps = {
            y: point.y,
            width: point.x - rect.x,
            height: rect.y + rect.height - point.y
          };
          break;
        case 3: // right-center
          newProps = {
            width: point.x - rect.x
          };
          break;
        case 4: // bottom-right
          newProps = {
            width: point.x - rect.x,
            height: point.y - rect.y
          };
          break;
        case 5: // bottom-center
          newProps = {
            height: point.y - rect.y
          };
          break;
        case 6: // bottom-left
          newProps = {
            x: point.x,
            width: rect.x + rect.width - point.x,
            height: point.y - rect.y
          };
          break;
        case 7: // left-center
          newProps = {
            x: point.x,
            width: rect.x + rect.width - point.x
          };
          break;
        default:
          break;
      }
      
      // Ensure width and height are not negative
      if (newProps.width && newProps.width < 0) {
        if ([0, 6, 7].includes(activeAnchorIndex)) {
          newProps.x = rect.x + rect.width;
          newProps.width = Math.abs(newProps.width);
        } else {
          newProps.width = 1;
        }
      }
      
      if (newProps.height && newProps.height < 0) {
        if ([0, 1, 2].includes(activeAnchorIndex)) {
          newProps.y = rect.y + rect.height;
          newProps.height = Math.abs(newProps.height);
        } else {
          newProps.height = 1;
        }
      }
      
      // Update annotations array
      const updatedAnnotations = annotations.map((ann) => {
        if (ann.id === selectedAnnotation.id) {
          return { ...ann, ...newProps };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      setSelectedAnnotation({ ...selectedAnnotation, ...newProps });
      
      // Set cursor
      stage.container().style.cursor = anchor.cursor;
      return;
    }
    
    // Handle drawing new annotation
    if (isDrawing && currentAnnotation) {
      const point = stage.getPointerPosition();
      if (!point) return;
      
      const newWidth = point.x - currentAnnotation.x;
      const newHeight = point.y - currentAnnotation.y;
      
      setCurrentAnnotation({
        ...currentAnnotation,
        width: newWidth,
        height: newHeight,
      });
      
      // Set cursor to drawing
      stage.container().style.cursor = 'crosshair';
      return;
    }
    
    // Default cursor
    stage.container().style.cursor = 'default';
  };

  const handleStageMouseUp = () => {
    // Only allow interaction in ANNOTATE mode
    if (mode !== MODE.ANNOTATE) return;
    
    // Handle end of dragging
    if (isDragging) {
      setIsDragging(false);
      setDragStartPosition(null);
      return;
    }
    
    if (activeAnchorIndex !== null) {
      setActiveAnchorIndex(null);
      isAnchorDraggingRef.current = false;
      return;
    }
    
    if (!isDrawing || !currentAnnotation) return;
    
    console.log('Finishing drawing, current annotation:', currentAnnotation);
    
    // If the rectangle is too small, discard it
    if (Math.abs(currentAnnotation.width) < 5 || Math.abs(currentAnnotation.height) < 5) {
      setCurrentAnnotation(null);
      setIsDrawing(false);
      console.log('Discarding too small annotation');
      return;
    }
    
    // Ensure positive width/height
    let annotation = { ...currentAnnotation };
    if (annotation.width < 0) {
      annotation.x += annotation.width;
      annotation.width = Math.abs(annotation.width);
    }
    
    if (annotation.height < 0) {
      annotation.y += annotation.height;
      annotation.height = Math.abs(annotation.height);
    }
    
    // Add defectType and defectTypeId with undefined values for new annotations
    annotation.defectType = 'Select type';
    annotation.defectTypeId = null;
    annotation.status = 'draft'; // Default status for new manual annotations
    annotation.isAiGenerated = false; // New manual annotations are not AI generated
    
    console.log('Adding new annotation:', annotation);
    setAnnotations([...annotations, annotation]);
    // Automatically select the new annotation
    setSelectedAnnotation(annotation);
    setCurrentAnnotation(null);
    setIsDrawing(false);
    
    // Notify user to select a defect type
    setApiStatus({
      open: true,
      severity: 'warning',
      message: 'Please select a defect type before saving - this is mandatory'
    });
  };

  const handleAnnotationClick = (annotation) => {
    // Only allow selecting in ANNOTATE mode
    if (mode !== MODE.ANNOTATE) return;
    
    setSelectedAnnotation(annotation);
  };
  
  const handleAnnotationDragStart = (e, annotation) => {
    // Only allow dragging in ANNOTATE mode
    if (mode !== MODE.ANNOTATE) return;
    
    // Prevent event bubbling to avoid triggering stage mousedown
    e.cancelBubble = true;
    
    // Only allow dragging if no anchor point is active
    if (activeAnchorIndex !== null) return;
    
    const stage = stageRef.current;
    if (!stage) return;
    
    const point = stage.getPointerPosition();
    if (!point) return;
    
    // Store initial position for calculating deltas
    setDragStartPosition(point);
    
    // Store the annotation's starting position to calculate the new position during drag
    setSelectedAnnotation({
      ...annotation,
      startX: annotation.x,
      startY: annotation.y
    });
    
    setIsDragging(true);
    
    // Set cursor to move
    stage.container().style.cursor = 'move';
  };
  
  const handleAnchorMouseDown = (e, anchorIndex) => {
    // Only allow interaction in ANNOTATE mode
    if (mode !== MODE.ANNOTATE) return;
    
    e.cancelBubble = true; // Stop event bubbling
    isAnchorDraggingRef.current = true;
    setActiveAnchorIndex(anchorIndex);
  };

  const handleDeleteSelected = async () => {
    if (!selectedAnnotation) return;
    
    try {
      // Check if this is a saved annotation (has a numeric ID)
      const boundingBoxId = parseInt(selectedAnnotation.id);
      
      if (!isNaN(boundingBoxId)) {
        // This is a saved annotation, delete it from the database
        setApiStatus({
          open: true,
          severity: 'info',
          message: 'Deleting annotation...'
        });
        
        // Call the API to delete the bounding box
        try {
          await boundingBoxApi.delete(boundingBoxId);
          
          // After deleting, check if this was the last annotation for this photo
          if (photo && photo.id) {
            try {
              // Get remaining bounding boxes for this photo
              const remainingBoxes = await boundingBoxApi.getAll({ photo_id: photo.id });
              
              // If there are no remaining boxes, update the photo has_annotation status via API
              if (remainingBoxes.length === 0) {
                try {
                  console.log('No annotations remaining for photo, updating has_annotation status');
                  // Use the new endpoint to update the photo's has_annotation flag
                  await api.patch(`/inspections/photos/${photo.id}`, {
                    has_annotation: false
                  });
                } catch (photoUpdateError) {
                  console.error('Error updating photo annotation status:', photoUpdateError);
                  // Continue anyway, as the backend trigger should handle this
                }
              }
            } catch (checkError) {
              console.error('Error checking remaining annotations:', checkError);
              // Continue anyway since the annotation was successfully deleted
            }
          }
          
          setApiStatus({
            open: true,
            severity: 'success',
            message: 'Annotation deleted successfully'
          });
          
          // Notify parent component that data has changed
          if (onDataMutated) {
            onDataMutated();
          }
          
          // Remove from local state
          const filteredAnnotations = annotations.filter(
            (ann) => ann.id !== selectedAnnotation.id
          );
          
          setAnnotations(filteredAnnotations);
          setSelectedAnnotation(null);
        } catch (apiError) {
          // Handle API error with detailed message
          let errorMessage = 'Unknown error';
          
          // Extract detailed error message from API response if available
          if (apiError.response?.data?.error) {
            errorMessage = typeof apiError.response.data.error === 'object'
              ? JSON.stringify(apiError.response.data.error)
              : apiError.response.data.error;
          } else if (apiError.message) {
            errorMessage = apiError.message;
          }
          
          console.error('Error deleting annotation:', apiError);
          setApiStatus({
            open: true,
            severity: 'error',
            message: errorMessage
          });
        }
      } else {
        // Not a saved annotation, just remove from local state
        const filteredAnnotations = annotations.filter(
          (ann) => ann.id !== selectedAnnotation.id
        );
        
        setAnnotations(filteredAnnotations);
        setSelectedAnnotation(null);
      }
    } catch (error) {
      console.error('Error in delete process:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error: ${error.message || 'Unknown error occurred'}`
      });
    }
  };

  const handleSaveAnnotations = async () => {
    if (!photo?.id) {
      setApiStatus({
        open: true,
        severity: 'error',
        message: 'Cannot save annotations: Missing photo ID'
      });
      return;
    }

    // The validation logic has been moved inside the loops to allow for partial saves.

    try {
      // Calculate scaling factors between display image and original full resolution
      const { width: saveDisplayW, height: saveDisplayH } = getDisplayDimensions(image, stageSize);
      const scaleFactorX = image && photo ? (photo.width || image.naturalWidth || 1) / (saveDisplayW || 1) : 1;
      const scaleFactorY = image && photo ? (photo.height || image.naturalHeight || 1) / (saveDisplayH || 1) : 1;

      // Get the image position offset
      const imagePositionX = image ? (stageSize.width - saveDisplayW) / 2 : 0;
      const imagePositionY = image ? (stageSize.height - saveDisplayH) / 2 : 0;
      
      // Set API loading state
      setApiStatus({
        open: true,
        severity: 'info',
        message: 'Saving annotations...'
      });
      
      // Separate annotations into existing and new
      const existingAnnotations = [];
      const newAnnotations = [];
      
      annotations.forEach(annotation => {
        const isExistingAnnotation = !isNaN(parseInt(annotation.id)) && annotation.originalX1 !== undefined;
        if (isExistingAnnotation) {
          existingAnnotations.push(annotation);
        } else {
          newAnnotations.push(annotation);
        }
      });
      
      console.log(`Processing ${existingAnnotations.length} existing and ${newAnnotations.length} new annotations`);
      
      // Track operation counts
      let newSaveCount = 0;
      let updateCount = 0;
      let skipCount = 0;
      let failCount = 0;
      
      // 1. Process existing annotations that have been modified
      for (const annotation of existingAnnotations) {
        try {
          // Calculate relative position (removing stage offsets)
          const relativeX = annotation.x - imagePositionX;
          const relativeY = annotation.y - imagePositionY;
          
          // Convert to original image coordinates
          const newX1 = Math.round(relativeX * scaleFactorX);
          const newY1 = Math.round(relativeY * scaleFactorY);
          const newX2 = Math.round((relativeX + annotation.width) * scaleFactorX);
          const newY2 = Math.round((relativeY + annotation.height) * scaleFactorY);
          
          // Check if the coordinates have changed (allow a small tolerance for rounding)
          const hasPositionChanged = 
            Math.abs(newX1 - annotation.originalX1) > 2 ||
            Math.abs(newY1 - annotation.originalY1) > 2 ||
            Math.abs(newX2 - annotation.originalX2) > 2 ||
            Math.abs(newY2 - annotation.originalY2) > 2;
          
          // Check if defect type or severity level has changed
          const hasDefectTypeChanged = annotation.isModified === true || 
            annotation.defectTypeId !== annotation.originalDefectTypeId;
          
          // Check if severity level has changed
          const hasSeverityLevelChanged = 
            annotation.severityLevelId !== annotation.originalSeverityLevelId;
          
          if (hasPositionChanged || hasDefectTypeChanged || hasSeverityLevelChanged) {
            // Update the existing annotation if it changed
            try {
              console.log(`Updating annotation ID ${annotation.id}:`, {
                old: {
                  x1: annotation.originalX1,
                  y1: annotation.originalY1,
                  x2: annotation.originalX2,
                  y2: annotation.originalY2,
                  defect_type_id: annotation.originalDefectTypeId || 'not_recorded',
                  severity_level_id: annotation.originalSeverityLevelId || 'not_recorded'
                },
                new: {
                  x1: newX1,
                  y1: newY1,
                  x2: newX2,
                  y2: newY2,
                  defect_type_id: annotation.defectTypeId,
                  severity_level_id: annotation.severityLevelId
                }
              });
              
              const updateData = {
                x1: newX1,
                y1: newY1,
                x2: newX2,
                y2: newY2
              };
              
              // Add defect_type_id to the update if it changed
              if (hasDefectTypeChanged) {
                updateData.defect_type_id = annotation.defectTypeId;
              }
              
              // Add severity_level_id to the update if it changed or exists
              if (hasSeverityLevelChanged || annotation.severityLevelId) {
                updateData.severity_level_id = annotation.severityLevelId;
              }
              
              // Ensure ID is a valid number - if it's a string (like from Date.now()), it could be invalid
              let boundingBoxId = annotation.id;
              
              // Check if ID needs to be parsed as integer
              if (typeof boundingBoxId === 'string') {
                try {
                  // If the ID is just a numeric string, parse it correctly
                  if (/^\d+$/.test(boundingBoxId)) {
                    boundingBoxId = parseInt(boundingBoxId, 10);
                  } else {
                    throw new Error('Invalid ID format');
                  }
                } catch (parseError) {
                  console.error(`Could not parse ID "${boundingBoxId}":`, parseError);
                  throw new Error(`Invalid annotation ID format: ${boundingBoxId}`);
                }
              }
              
              console.log(`Sending update request for boundingBoxId: ${boundingBoxId} (type: ${typeof boundingBoxId}) with data:`, updateData);
              
              try {
                const response = await boundingBoxApi.update(boundingBoxId, updateData);
                console.log(`Successfully updated annotation ID ${annotation.id}:`, response);
                
                // Update the local annotation with updated_by information if returned in the response
                if (response) {
                  annotation.updated_by = response.updated_by || annotation.updated_by;
                  annotation.updated_by_full_name = response.updated_by_full_name || annotation.updated_by_full_name;
                  annotation.updated_by_email = response.updated_by_email || annotation.updated_by_email;
                  annotation.updated_at = response.updated_at || annotation.updated_at;
                  annotation.originalDefectTypeId = annotation.defectTypeId; // Update the original defect type ID
                  annotation.originalSeverityLevelId = annotation.severityLevelId; // Update the original severity level ID
                  annotation.isModified = false; // Reset modified flag
                }
                
                updateCount++;
              } catch (apiError) {
                // Special handling for API errors
                let detailedErrorMessage = 'Unknown API error';
                if (apiError.response?.data?.error) {
                    detailedErrorMessage = (typeof apiError.response.data.error === 'object' && apiError.response.data.error !== null)
                        ? JSON.stringify(apiError.response.data.error)
                        : apiError.response.data.error.toString();
                } else if (apiError.message) {
                    detailedErrorMessage = apiError.message;
                }

                const statusCode = apiError.response?.status;

                console.error(`API error (${statusCode}) updating annotation ID ${boundingBoxId}:`, detailedErrorMessage);
                
                if (statusCode === 404) {
                  // Handle 404 Not Found - the box might have been deleted on the server
                  setApiStatus({
                    open: true,
                    severity: 'error',
                    message: `Cannot update annotation #${annotation.id}: Box not found on server`
                  });
                } else {
                  // Handle other API errors
                  setApiStatus({
                    open: true,
                    severity: 'error',
                    message: `Error updating annotation #${annotation.id}: ${detailedErrorMessage}`
                  });
                }

                throw apiError; // Re-throw to be caught by the outer catch
              }
            } catch (error) {
              console.error('Error updating existing annotation:', error);
              failCount++;
            }
          } else {
            // Skip without attempting to save
            skipCount++;
          }
        } catch (error) {
          console.error('Error processing existing annotation:', error);
          failCount++;
        }
      }
      
      // 2. Process new annotations
      for (const annotation of newAnnotations) {
        // Only save new annotations that are complete with a defect type and severity
        if (!annotation.defectTypeId || !annotation.severityLevelId) {
          console.log(`Skipping unsaved new annotation (temp ID: ${annotation.id}) because it's incomplete.`);
          skipCount++;
          continue; // Skip to the next annotation
        }
        
        try {
          // Calculate relative position (removing stage offsets)
          const relativeX = annotation.x - imagePositionX;
          const relativeY = annotation.y - imagePositionY;
          
          // Convert to original image coordinates
          const x1 = Math.round(relativeX * scaleFactorX);
          const y1 = Math.round(relativeY * scaleFactorY);
          const x2 = Math.round((relativeX + annotation.width) * scaleFactorX);
          const y2 = Math.round((relativeY + annotation.height) * scaleFactorY);
          
          // Prepare data for API
          const boundingBoxData = {
            photo_id: photo.id,
            defect_type_id: annotation.defectTypeId,
            x1,
            y1,
            x2,
            y2,
            status: 'draft',
            is_ai_generated: false,
            confidence_score: null,
            notes: annotation.notes || '',
            created_by_full_name: annotation.created_by_full_name || null,
            created_by_email: annotation.created_by_email || null,
            updated_by_full_name: annotation.updated_by_full_name || null,
            updated_by_email: annotation.updated_by_email || null
          };
          
          // Add severity_level_id if present
          if (annotation.severityLevelId) {
            boundingBoxData.severity_level_id = annotation.severityLevelId;
            console.log(`[DEBUG] Including severity_level_id ${annotation.severityLevelId} for new annotation (temp ID: ${annotation.id})`);
          } else {
             // This case should ideally not be hit due to the check at the start
             console.warn(`[DEBUG] Severity Level ID is missing for new annotation (temp ID: ${annotation.id}) just before API call.`);
          }
          
          console.log('[DEBUG] Saving new bounding box with data:', boundingBoxData);
          
          // Save to database via API service - new annotation
          const response = await boundingBoxApi.create(boundingBoxData);
          console.log('[DEBUG] New bounding box saved successfully:', response);
          
          // Update local state with the new ID and other server-returned data
          if (response && response.id) {
            annotation.id = response.id.toString();
            annotation.originalX1 = x1;
            annotation.originalY1 = y1;
            annotation.originalX2 = x2;
            annotation.originalY2 = y2;
            annotation.originalDefectTypeId = annotation.defectTypeId;
            annotation.originalSeverityLevelId = annotation.severityLevelId;
            annotation.isModified = false;
          }
          
          newSaveCount++;
        } catch (error) {
          console.error('Error saving new annotation:', error);
          failCount++;
        }
      }
      
      // Set final status message
      if (failCount === 0) {
        let message = '';
        const parts = [];
        
        if (newSaveCount > 0) parts.push(`created ${newSaveCount} new`);
        if (updateCount > 0) parts.push(`updated ${updateCount} existing`);
        if (skipCount > 0) parts.push(`skipped ${skipCount} unchanged`);
        
        message = `Success: ${parts.join(', ')} annotation(s)`;
        
        setApiStatus({
          open: true,
          severity: 'success',
          message
        });
        
        // If new annotations were created, update the photo's has_annotation flag to true
        if (newSaveCount > 0 && photo?.id) {
          try {
            console.log('Setting photo has_annotation status to true');
            // Update the photo's has_annotation flag
            await api.patch(`/inspections/photos/${photo.id}`, {
              has_annotation: true
            });
          } catch (photoUpdateError) {
            console.error('Error updating photo annotation status:', photoUpdateError);
            // Continue anyway, as this is just an optimization
          }
        }
        
        // Reload annotations to get the latest data with server-assigned IDs
        if (photo.id) {
          loadExistingAnnotations(photo.id, image);
        }
      } else {
        setApiStatus({
          open: true,
          severity: 'warning',
          message: `Partial success: ${newSaveCount + updateCount} saved, ${failCount} failed`
        });
      }
      
      // Log for debugging
      console.log('Annotation save summary:', {
        newSaveCount,
        updateCount,
        skipCount,
        failCount,
        scaleFactorX,
        scaleFactorY
      });
    } catch (error) {
      console.error('Error in save process:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error: ${error.message || 'Unknown error occurred'}`
      });
    }
  };

  // Add handler to close the snackbar
  const handleSnackbarClose = () => {
    setApiStatus(prev => ({ ...prev, open: false }));
  };

  // Updated handleClearAll to show confirmation dialog instead of window.confirm
  const handleClearAll = () => {
    if (!photo?.id) {
      setApiStatus({
        open: true,
        severity: 'warning',
        message: 'No photo selected or photo ID is missing.'
      });
      return;
    }

    // Show custom confirmation dialog
    setClearAllConfirmOpen(true);
  };

  // Function to handle the actual deletion after confirmation
  const confirmClearAll = async () => {
    setClearAllConfirmOpen(false); // Close dialog
    setApiStatus({ open: true, severity: 'info', message: 'Deleting all annotations...' });

    let deletedCount = 0;
    let failedCount = 0;

    // Filter out annotations that are not yet saved (e.g., new ones not yet sent to backend)
    const savedAnnotations = annotations.filter(ann => !isNaN(parseInt(ann.id)));

    if (savedAnnotations.length === 0) {
      setAnnotations([]); // Clear any unsaved local annotations
      setSelectedAnnotation(null);
      setApiStatus({ open: true, severity: 'info', message: 'No saved annotations to delete.' });
      return;
    }

    for (const annotation of savedAnnotations) {
      try {
        const boundingBoxId = parseInt(annotation.id);
        if (!isNaN(boundingBoxId)) { // Double check it's a valid ID
          await boundingBoxApi.delete(boundingBoxId);
          deletedCount++;
        }
      } catch (error) {
        failedCount++;
        console.error(`Failed to delete annotation ID ${annotation.id}:`, error);
        // Optionally, collect individual error messages
      }
    }

    if (failedCount > 0) {
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Failed to delete ${failedCount} annotation(s). ${deletedCount} deleted successfully.`
      });
    } else {
      setApiStatus({
        open: true,
        severity: 'success',
        message: `Successfully deleted ${deletedCount} annotation(s).`
      });
    }

    // Clear local state regardless of individual failures, as some might have succeeded
    setAnnotations([]);
    setSelectedAnnotation(null);

    // The backend /api/bounding-boxes/:id DELETE route should handle
    // updating the photo's has_annotation status when the last box is removed.
    // However, we can explicitly call it if all were cleared successfully client-side.
    if (failedCount === 0 && deletedCount > 0 && photo && photo.id) {
      try {
        await api.patch(`/inspections/photos/${photo.id}`, {
          has_annotation: false
        });
        console.log('Photo has_annotation status explicitly set to false after clearing all.');
      } catch (photoUpdateError) {
        console.error('Error updating photo has_annotation status after clearing all:', photoUpdateError);
      }
    }
    
    // Notify parent component that data has changed
    if (onDataMutated) {
      onDataMutated();
    }
  };

  // Function to close the confirmation dialog
  const closeClearAllConfirm = () => {
    setClearAllConfirmOpen(false);
  };

  // Handle opening status change menu
  const handleStatusMenuOpen = (e, annotation) => {
    e.stopPropagation();
    setStatusMenuAnchor({ top: e.clientY, left: e.clientX });
    setSelectedStatusAnnotation(annotation);
  };

  // Handle closing status change menu
  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedStatusAnnotation(null);
  };

  // Handle opening defect type change menu
  const handleDefectTypeMenuOpen = (e, annotation) => {
    e.stopPropagation();
    // Add check for validated status
    if (annotation.status === 'validated') {
      setApiStatus({
        open: true,
        severity: 'info',
        message: 'Defect type cannot be changed for validated annotations.'
      });
      return;
    }
    setDefectTypeMenuAnchor({ top: e.clientY, left: e.clientX });
    setSelectedDefectTypeAnnotation(annotation);
  };

  // Handle closing defect type change menu
  const handleDefectTypeMenuClose = () => {
    setDefectTypeMenuAnchor(null);
    setSelectedDefectTypeAnnotation(null);
  };

  // Handle status change - now opens notes dialog instead of immediate update
  const handleStatusChange = async (newStatus) => {
    // Close menu
    setStatusMenuAnchor(null);
    
    if (!selectedStatusAnnotation) return;

    // Find the most up-to-date version of the annotation from the state
    const annotationToUpdate = annotations.find(ann => ann.id === selectedStatusAnnotation.id);
    if (!annotationToUpdate) {
        console.error("Could not find annotation to update in state");
        setApiStatus({ open: true, severity: 'error', message: 'Could not find annotation to update.' });
        return;
    }
    
    const boundingBoxId = parseInt(annotationToUpdate.id);
    
    if (isNaN(boundingBoxId)) {
      setApiStatus({
        open: true,
        severity: 'error',
        message: 'Cannot update status: This annotation has not been saved yet.'
      });
      return;
    }

    // If validating, ensure a severity level is selected.
    if (newStatus === 'validated' && !annotationToUpdate.severityLevelId) {
      setApiStatus({
        open: true,
        severity: 'error',
        message: 'Cannot validate a defect without a severity level. Please select a severity first.'
      });
      return;
    }

    // Open notes dialog for validation/rejection
    if (newStatus === 'validated' || newStatus === 'rejected') {
      setPendingStatusChange({ annotation: annotationToUpdate, newStatus });
      setStatusRemarks(annotationToUpdate.notes || ''); // Pre-fill with existing notes
      setStatusNotesDialogOpen(true);
      return;
    }

    // Create a full payload for the update, including status and severity
    const updatePayload = {
      status: newStatus,
      severity_level_id: annotationToUpdate.severityLevelId
    };
    
    try {
      setApiStatus({
        open: true,
        severity: 'info',
        message: `Updating status to ${newStatus}...`
      });
      
      // Update status and severity via API in a single call
      const response = await boundingBoxApi.update(boundingBoxId, updatePayload);
      
      // Update in local state
      const updatedAnnotations = annotations.map(ann => {
        if (ann.id === annotationToUpdate.id) {
          return {
            ...ann,
            status: newStatus,
            updated_by: response.updated_by || ann.updated_by,
            updated_by_full_name: response.updated_by_full_name || ann.updated_by_full_name,
            updated_by_email: response.updated_by_email || ann.updated_by_email,
            updated_at: response.updated_at || ann.updated_at
          };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      
      if (selectedAnnotation && selectedAnnotation.id === annotationToUpdate.id) {
        setSelectedAnnotation(prev => ({
          ...prev,
          status: newStatus,
          updated_by: response.updated_by || prev.updated_by,
          updated_by_full_name: response.updated_by_full_name || prev.updated_by_full_name,
          updated_by_email: response.updated_by_email || prev.updated_by_email,
          updated_at: response.updated_at || prev.updated_at
        }));
      }
      
      setSelectedStatusAnnotation(null);
      
      setApiStatus({
        open: true,
        severity: 'success',
        message: `Status updated to ${newStatus}`
      });

      // Notify parent component that data has changed
      if (onDataMutated) {
        onDataMutated();
      }

    } catch (error) {
      console.error('Error updating status:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error updating status: ${error.message || 'Unknown error'}`
      });
    }
  };

  // Handle closing the status notes dialog
  const handleCancelStatusChange = () => {
    setStatusNotesDialogOpen(false);
    setPendingStatusChange(null);
    setStatusRemarks('');
  };

  // Handle confirming the status change with notes
  const handleConfirmStatusChange = async () => {
    if (!pendingStatusChange) return;

    const { annotation, newStatus } = pendingStatusChange;
    const boundingBoxId = parseInt(annotation.id);

    // Close dialog first
    setStatusNotesDialogOpen(false);

    // Create payload with notes — include defect_type_id so local changes are persisted
    const updatePayload = {
      status: newStatus,
      defect_type_id: annotation.defectTypeId,
      severity_level_id: annotation.severityLevelId,
      notes: statusRemarks.trim() || null // Include notes (null if empty)
    };

    try {
      setApiStatus({
        open: true,
        severity: 'info',
        message: `Updating status to ${newStatus}...`
      });

      // Update via API
      const response = await boundingBoxApi.update(boundingBoxId, updatePayload);

      // Update local state
      const updatedAnnotations = annotations.map(ann => {
        if (ann.id === annotation.id) {
          return {
            ...ann,
            status: newStatus,
            notes: statusRemarks.trim() || ann.notes, // Update notes in local state
            defect_id: response.defect_id || ann.defect_id, // Update defect ID if available
            has_defect: response.has_defect !== undefined ? response.has_defect : ann.has_defect, // Update defect flag
            updated_by: response.updated_by || ann.updated_by,
            updated_by_full_name: response.updated_by_full_name || ann.updated_by_full_name,
            updated_by_email: response.updated_by_email || ann.updated_by_email,
            updated_at: response.updated_at || ann.updated_at
          };
        }
        return ann;
      });

      setAnnotations(updatedAnnotations);

      if (selectedAnnotation && selectedAnnotation.id === annotation.id) {
        setSelectedAnnotation(prev => ({
          ...prev,
          status: newStatus,
          notes: statusRemarks.trim() || prev.notes,
          defect_id: response.defect_id || prev.defect_id, // Update defect ID if available
          has_defect: response.has_defect !== undefined ? response.has_defect : prev.has_defect, // Update defect flag
          updated_by: response.updated_by || prev.updated_by,
          updated_by_full_name: response.updated_by_full_name || prev.updated_by_full_name,
          updated_by_email: response.updated_by_email || prev.updated_by_email,
          updated_at: response.updated_at || prev.updated_at
        }));
      }

      setApiStatus({
        open: true,
        severity: 'success',
        message: `Status updated to ${newStatus}${statusRemarks.trim() ? ' with notes' : ''}`
      });

      // Notify parent component that data has changed
      if (onDataMutated) {
        onDataMutated();
      }

    } catch (error) {
      console.error('Error updating status:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error updating status: ${error.message || 'Unknown error'}`
      });
    } finally {
      // Clean up
      setPendingStatusChange(null);
      setStatusRemarks('');
    }
  };

  // Handle opening notes editing dialog
  const handleEditNotes = (annotation) => {
    const annotationToEdit = annotations.find(ann => ann.id === annotation.id);
    if (!annotationToEdit) return;

    setEditingNotesAnnotation(annotationToEdit);
    setEditingNotes(annotationToEdit.notes || '');
    setNotesEditDialogOpen(true);
  };

  // Handle closing notes editing dialog
  const handleCancelNotesEdit = () => {
    setNotesEditDialogOpen(false);
    setEditingNotesAnnotation(null);
    setEditingNotes('');
  };

  // Handle saving notes edit
  const handleSaveNotesEdit = async () => {
    if (!editingNotesAnnotation) return;

    const boundingBoxId = parseInt(editingNotesAnnotation.id);

    if (isNaN(boundingBoxId)) {
      setApiStatus({
        open: true,
        severity: 'error',
        message: 'Cannot update notes: This annotation has not been saved yet.'
      });
      return;
    }

    // Close dialog first
    setNotesEditDialogOpen(false);

    try {
      setApiStatus({
        open: true,
        severity: 'info',
        message: 'Updating notes...'
      });

      // Update only notes via API
      const response = await boundingBoxApi.update(boundingBoxId, {
        notes: editingNotes.trim() || null
      });

      // Update local state
      const updatedAnnotations = annotations.map(ann => {
        if (ann.id === editingNotesAnnotation.id) {
          return {
            ...ann,
            notes: editingNotes.trim() || '', // Update notes in local state
            updated_by: response.updated_by || ann.updated_by,
            updated_by_full_name: response.updated_by_full_name || ann.updated_by_full_name,
            updated_by_email: response.updated_by_email || ann.updated_by_email,
            updated_at: response.updated_at || ann.updated_at
          };
        }
        return ann;
      });

      setAnnotations(updatedAnnotations);

      if (selectedAnnotation && selectedAnnotation.id === editingNotesAnnotation.id) {
        setSelectedAnnotation(prev => ({
          ...prev,
          notes: editingNotes.trim() || '',
          updated_by: response.updated_by || prev.updated_by,
          updated_by_full_name: response.updated_by_full_name || prev.updated_by_full_name,
          updated_by_email: response.updated_by_email || prev.updated_by_email,
          updated_at: response.updated_at || prev.updated_at
        }));
      }

      setApiStatus({
        open: true,
        severity: 'success',
        message: 'Notes updated successfully'
      });

      // Notify parent component that data has changed
      if (onDataMutated) {
        onDataMutated();
      }

    } catch (error) {
      console.error('Error updating notes:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error updating notes: ${error.message || 'Unknown error'}`
      });
    } finally {
      // Clean up
      setEditingNotesAnnotation(null);
      setEditingNotes('');
    }
  };

  // Auto-validate: when an annotation has both defect type and severity set,
  // automatically save (if needed) and open the validation notes dialog.
  const triggerAutoValidate = async (annotationToValidate) => {
    if (!annotationToValidate || !annotationToValidate.defectTypeId || !annotationToValidate.severityLevelId
        || annotationToValidate.status !== 'draft' || viewOnlyMode) {
      return false;
    }

    const boundingBoxId = parseInt(annotationToValidate.id);

    if (!isNaN(boundingBoxId)) {
      // Already saved in DB — open validation dialog directly.
      // The validate API call sends severity_level_id along with the status change.
      setPendingStatusChange({ annotation: annotationToValidate, newStatus: 'validated' });
      setStatusRemarks(annotationToValidate.notes || '');
      setStatusNotesDialogOpen(true);
      return true;
    }

    // Unsaved annotation — save to DB first so we get a real ID for validation
    if (!photo?.id || !image) {
      setApiStatus({ open: true, severity: 'error', message: 'Cannot auto-save: missing photo or image data.' });
      return false;
    }

    setApiStatus({ open: true, severity: 'info', message: 'Saving annotation...' });

    // Convert display coordinates to original image coordinates
    const { width: dw, height: dh } = getDisplayDimensions(image, stageSize);
    const sfx = (photo.width || image.naturalWidth || 1) / (dw || 1);
    const sfy = (photo.height || image.naturalHeight || 1) / (dh || 1);
    const ipx = (stageSize.width - dw) / 2;
    const ipy = (stageSize.height - dh) / 2;
    const rx = annotationToValidate.x - ipx;
    const ry = annotationToValidate.y - ipy;
    const x1 = Math.round(rx * sfx);
    const y1 = Math.round(ry * sfy);
    const x2 = Math.round((rx + annotationToValidate.width) * sfx);
    const y2 = Math.round((ry + annotationToValidate.height) * sfy);

    const response = await boundingBoxApi.create({
      photo_id: photo.id,
      defect_type_id: annotationToValidate.defectTypeId,
      severity_level_id: annotationToValidate.severityLevelId,
      x1, y1, x2, y2,
      status: 'draft',
      is_ai_generated: false,
      confidence_score: null,
      notes: annotationToValidate.notes || '',
      created_by_full_name: annotationToValidate.created_by_full_name || null,
      created_by_email: annotationToValidate.created_by_email || null,
      updated_by_full_name: annotationToValidate.updated_by_full_name || null,
      updated_by_email: annotationToValidate.updated_by_email || null
    });

    if (!response?.id) {
      setApiStatus({ open: true, severity: 'error', message: 'Failed to save annotation.' });
      return false;
    }

    const realId = response.id.toString();
    const savedAnn = {
      ...annotationToValidate,
      id: realId,
      originalX1: x1, originalY1: y1, originalX2: x2, originalY2: y2,
      originalDefectTypeId: annotationToValidate.defectTypeId,
      originalSeverityLevelId: annotationToValidate.severityLevelId,
      isModified: false
    };

    // Update local state with the real DB ID
    setAnnotations(prev => prev.map(ann => ann.id === annotationToValidate.id ? savedAnn : ann));
    if (selectedAnnotation && selectedAnnotation.id === annotationToValidate.id) {
      setSelectedAnnotation(savedAnn);
    }

    // Mark photo as having annotations
    try {
      await api.patch(`/inspections/photos/${photo.id}`, { has_annotation: true });
    } catch (e) {
      console.error('Error updating photo annotation status:', e);
    }

    // Open validation dialog
    setPendingStatusChange({ annotation: savedAnn, newStatus: 'validated' });
    setStatusRemarks(savedAnn.notes || '');
    setStatusNotesDialogOpen(true);
    return true;
  };

  // Handle defect type change
  const handleDefectTypeChange = async (newDefectTypeId, newDefectTypeName) => {
    // Close menu
    setDefectTypeMenuAnchor(null);
    
    if (!selectedDefectTypeAnnotation) return;
    
    try {
      // Check if this is a saved annotation (has a valid numeric ID in the database)
      const boundingBoxId = parseInt(selectedDefectTypeAnnotation.id);
      const isExistingAnnotation = !isNaN(boundingBoxId);
      
      // Update in local state only - we'll save to database when user clicks save button
      const updatedAnnotations = annotations.map(ann => {
        if (ann.id === selectedDefectTypeAnnotation.id) {
          return { 
            ...ann,
            defectTypeId: newDefectTypeId,
            defectType: newDefectTypeName,
            // Mark the annotation as modified if it's an existing one
            isModified: isExistingAnnotation ? true : undefined
          };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      
      if (selectedAnnotation && selectedAnnotation.id === selectedDefectTypeAnnotation.id) {
        setSelectedAnnotation(prev => ({ 
          ...prev,
          defectTypeId: newDefectTypeId,
          defectType: newDefectTypeName,
          // Mark the annotation as modified if it's an existing one
          isModified: isExistingAnnotation ? true : undefined
        }));
      }
      
      setSelectedDefectTypeAnnotation(null);
      
      // Find the annotation that was just updated in the new array
      const updatedAnn = updatedAnnotations.find(ann => ann.id === selectedDefectTypeAnnotation.id);

      if (updatedAnn && updatedAnn.severityLevelId) {
        // Both defect type and severity are set — auto-validate
        try {
          await triggerAutoValidate(updatedAnn);
        } catch (autoErr) {
          console.error('Auto-validate failed after defect type change:', autoErr);
          setApiStatus({ open: true, severity: 'error', message: `Auto-validate failed: ${autoErr.message || 'Unknown error'}` });
        }
      } else {
        // If severity is not set, prompt the user
        setApiStatus({
          open: true,
          severity: 'info',
          message: `Defect type set to ${newDefectTypeName}. Please select a severity level.`
        });
      }
    } catch (error) {
      console.error('Error updating defect type:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error updating defect type: ${error.message || 'Unknown error'}`
      });
    }
  };

  // This prevents image dragging which can interfere with drawing
  const handleImageDragStart = (e) => {
    e.preventDefault();
    return false;
  };
  
  // Retry loading the image if it failed
  const handleRetryLoad = () => {
    loadImage();
  };

  // Handle zooming to a specific annotation
  const handleZoomToAnnotation = (annotation) => {
    console.log('handleZoomToAnnotation called with:', annotation);
    if (!stageRef.current || !imageStateRef.current || stageSize.width === 0 || stageSize.height === 0) {
      console.log('handleZoomToAnnotation: Missing stage/image or stageSize not ready, deferring zoom');
      // Defer zoom until image/stage are ready
      zoomPendingRef.current = annotation;
      // Retry a couple of times automatically to cover first-open race
      if (zoomRetryRef.current < 3) {
        zoomRetryRef.current += 1;
        clearTimeout(zoomTimeoutRef.current);
        zoomTimeoutRef.current = setTimeout(() => {
          handleZoomToAnnotation(annotation);
        }, 50 * zoomRetryRef.current);
      }
      return;
    }
    
    // If already zoomed to this annotation, reset zoom
    if (isZoomedRef.current && selectedAnnotationRef.current && selectedAnnotationRef.current.id === annotation.id) {
      resetZoom();
      return;
    }
    
    // Select the annotation
    setSelectedAnnotation(annotation);
    
    // Calculate the center point of the annotation
    const annotationCenterX = annotation.x + annotation.width / 2;
    const annotationCenterY = annotation.y + annotation.height / 2;
    
    // Calculate the zoom level based on annotation size
    // We want the annotation to take up about 50% of the view
    const scaleX = stageSize.width / (annotation.width * 2);
    const scaleY = stageSize.height / (annotation.height * 2);
    const newZoom = Math.min(scaleX, scaleY, 5); // Limit max zoom to 5x
    
    // Calculate new position to center the annotation
    const newPositionX = (stageSize.width / 2) - (annotationCenterX * newZoom);
    const newPositionY = (stageSize.height / 2) - (annotationCenterY * newZoom);
    
    // Update state
    setZoomLevel(newZoom);
    setStagePosition({ x: newPositionX, y: newPositionY });
    setIsZoomed(true);
    isZoomedRef.current = true;
  };

  // Keep the zoom function ref updated to avoid stale closures
  useEffect(() => {
    handleZoomToAnnotationRef.current = handleZoomToAnnotation;
  });
  
  // Reset zoom to normal
  const resetZoom = () => {
    console.log('resetZoom called');
    setZoomLevel(1);
    setStagePosition({ x: 0, y: 0 });
    setIsZoomed(false);
    isZoomedRef.current = false;
  };

  // Add a safe close handler to ensure cleanup before closing the modal
  const handleSafeClose = () => {
    // Clear all state
    setImage(null);
    setAnnotations([]);
    setSelectedAnnotation(null);
    setCurrentAnnotation(null);
    setIsDrawing(false);
    setIsDragging(false);
    setActiveAnchorIndex(null);
    setDragStartPosition(null);
    setZoomLevel(1);
    setStagePosition({ x: 0, y: 0 });
    setIsZoomed(false);
    setFocusMode(FOCUS_MODE.OFF);
    
    // Now safe to close
    onClose();
  };

  // Add handlers for severity menu
  const handleSeverityMenuOpen = (e, annotation) => {
    e.stopPropagation();
    setSeverityMenuAnchor({ top: e.clientY, left: e.clientX });
    setSelectedSeverityAnnotation(annotation);
  };

  const handleSeverityMenuClose = () => {
    setSeverityMenuAnchor(null);
    setSelectedSeverityAnnotation(null);
  };

  const handleSeverityChange = async (severityId, severityName) => {
    // Close menu
    setSeverityMenuAnchor(null);
    
    if (!selectedSeverityAnnotation) return;
    
    try {
      // Update in local state only - we'll save to database when user clicks save button
      const updatedAnnotations = annotations.map(ann => {
        if (ann.id === selectedSeverityAnnotation.id) {
          return { 
            ...ann,
            severityLevelId: severityId,
            severityLevel: severityName,
            isModified: true
          };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      
      if (selectedAnnotation && selectedAnnotation.id === selectedSeverityAnnotation.id) {
        setSelectedAnnotation(prev => ({ 
          ...prev,
          severityLevelId: severityId,
          severityLevel: severityName,
          isModified: true
        }));
      }
      
      setSelectedSeverityAnnotation(null);
      
      // Find the annotation that was just updated in the new array
      const updatedAnnAfterSeverity = updatedAnnotations.find(ann => ann.id === selectedSeverityAnnotation.id);

      if (updatedAnnAfterSeverity && updatedAnnAfterSeverity.defectTypeId) {
        // Both defect type and severity are set — auto-validate
        try {
          await triggerAutoValidate(updatedAnnAfterSeverity);
        } catch (autoErr) {
          console.error('Auto-validate failed after severity change:', autoErr);
          setApiStatus({ open: true, severity: 'error', message: `Auto-validate failed: ${autoErr.message || 'Unknown error'}` });
        }
      } else {
        // If defect type is not set, prompt the user
        setApiStatus({
          open: true,
          severity: 'info',
          message: `Severity level set to ${severityName}. Please select a defect type.`
        });
      }
    } catch (error) {
      console.error('Error updating severity level:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error updating severity level: ${error.message || 'Unknown error'}`
      });
    }
  };

  // Wrapper component that handles both modal and fullscreen modes
  const ContentWrapper = fullScreenMode ? Box : Dialog;
  const contentWrapperProps = fullScreenMode
    ? {
        sx: {
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }
      }
    : {
        open: open,
        onClose: handleSafeClose,
        maxWidth: "lg",
        fullWidth: true,
      };

  // Don't render anything if not open (for non-fullscreen mode, Dialog handles this)
  if (!fullScreenMode && !open) return null;

  return (
    <>
      <ContentWrapper {...contentWrapperProps}>
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: 'background.paper',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Previous Navigation Button */}
          <Tooltip title={canGoPrevious ? "Previous image (←)" : "No previous image"}>
            <span>
              <IconButton 
                onClick={handlePrevious}
                disabled={!canGoPrevious}
                size="small"
                sx={{
                  bgcolor: canGoPrevious ? 'primary.main' : 'action.disabled',
                  color: canGoPrevious ? 'white' : 'text.disabled',
                  '&:hover': {
                    bgcolor: canGoPrevious ? 'primary.dark' : 'action.disabled',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabled',
                    color: 'text.disabled'
                  }
                }}
              >
                <ArrowBackIosIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </span>
          </Tooltip>

          <Box>
            <Typography variant="h6" component="div">
              Image Viewer
          </Typography>
          {photo && (
            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {photo.filename || photo.url?.split('/').pop() || photo.name || photo.fileName || 'Image'} 
              {image && ` • ${image.width} × ${image.height} px`}
              {photo.captureDate && ` • ${new Date(photo.captureDate).toLocaleDateString()}`}
            </Typography>
          )}
          
          {/* Photo Counter - Show only if we have multiple photos */}
          {photos.length > 1 && (
            <Typography variant="body2" sx={{ 
              color: 'text.secondary', 
              fontWeight: 500,
              backgroundColor: 'action.hover',
              px: 2,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem'
            }}>
              {currentIndex + 1} of {photos.length}
            </Typography>
          )}
        </Box>

        {/* Next Navigation Button */}
        <Tooltip title={canGoNext ? "Next image (→)" : "No next image"}>
          <span>
            <IconButton 
              onClick={handleNext}
              disabled={!canGoNext}
              size="small"
              sx={{
                bgcolor: canGoNext ? 'primary.main' : 'action.disabled',
                color: canGoNext ? 'white' : 'text.disabled',
                mr: 2,
                '&:hover': {
                  bgcolor: canGoNext ? 'primary.dark' : 'action.disabled',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabled',
                  color: 'text.disabled'
                }
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </span>
        </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={mode === MODE.ANNOTATE ? "Exit Annotation Mode" : "Enter Annotation Mode"}>
            <span> {/* Add span for Tooltip when IconButton is disabled */} 
              <IconButton
                onClick={toggleAnnotationMode}
                aria-label="annotation mode"
                color={mode === MODE.ANNOTATE ? "primary" : "default"}
                size="small"
                disabled={viewOnlyMode} // Disable button if viewOnlyMode is true
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={showSegmentation ? "Hide Segmentation" : "Show Segmentation"}>
            <IconButton
              onClick={() => setShowSegmentation(!showSegmentation)}
              aria-label="toggle segmentation visibility"
              color={showSegmentation ? "primary" : "default"}
              size="small"
            >
              {showSegmentation ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
          
          <Tooltip title={focusMode === FOCUS_MODE.OFF ? "Focus Mode - Hide Other Annotations" : "Exit Focus Mode"}>
            <span>
              <IconButton
                onClick={toggleFocusMode}
                aria-label="focus mode"
                color={focusMode !== FOCUS_MODE.OFF ? "secondary" : "default"}
                size="small"
                disabled={viewOnlyMode || annotations.length === 0}
              >
                <CenterFocusStrongIcon />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title="Delete Selected">
            <span>
              <IconButton 
                onClick={handleDeleteSelected} 
                disabled={!selectedAnnotation || mode !== MODE.ANNOTATE}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title="Clear All">
            <span>
              <IconButton 
                onClick={handleClearAll} 
                disabled={annotations.length === 0 || mode !== MODE.ANNOTATE}
                color="warning"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title="Save Annotations">
            <span>
              <IconButton 
                onClick={handleSaveAnnotations} 
                disabled={annotations.length === 0}
                color="primary"
                size="small"
              >
                <SaveIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Randomize Segmentation Colors Button */}
          <Tooltip title={useRandomColors ? "Reset to Default Colors" : "Randomize Segmentation Colors"}>
            <IconButton
              onClick={useRandomColors ? resetSegmentationColors : randomizeSegmentationColors}
              aria-label="randomize segmentation colors"
              color={useRandomColors ? "secondary" : "default"}
              size="small"
              disabled={annotations.length === 0 || !annotations.some(ann => ann.segmentationPolygon)}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  background: useRandomColors 
                    ? 'linear-gradient(45deg, #FF6B6B 0%, #4ECDC4 25%, #45B7D1 50%, #96CEB4 75%, #FFEAA7 100%)'
                    : 'linear-gradient(45deg, #757575 0%, #BDBDBD 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: 'white',
                  textShadow: '1px 1px 1px rgba(0,0,0,0.8)'
                }}
              >
                🎨
              </Box>
            </IconButton>
          </Tooltip>
          
            <IconButton edge="end" color="inherit" onClick={handleSafeClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isZoomed && (
            <Tooltip title="Reset Zoom">
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<ZoomOutIcon />}
                onClick={resetZoom}
              >
                Reset View
              </Button>
            </Tooltip>
          )}
        </Box>
        
        <Box 
          ref={containerRef}
          sx={{
            overflow: 'hidden',
            maxHeight: '70vh',
            height: '70vh',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 0,
            position: 'relative',
          }}
        >
          {/* Focus Mode Indicator */}
          {focusMode !== FOCUS_MODE.OFF && (
            <Box sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              bgcolor: 'secondary.main',
              color: 'white',
              px: 2,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 'bold',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: 2
            }}>
              <CenterFocusStrongIcon sx={{ fontSize: '1rem' }} />
              Focus Mode: {selectedAnnotation ? 
                `${annotations.findIndex(ann => ann.id === selectedAnnotation.id) + 1} of ${annotations.length}` : 
                'Selected Only'
              }
              {annotations.length > 1 && (
                <Typography variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
                  Press Tab to cycle
                </Typography>
              )}
              <IconButton 
                size="small" 
                onClick={() => setFocusMode(FOCUS_MODE.OFF)}
                sx={{ color: 'white', p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: '0.75rem' }} />
              </IconButton>
            </Box>
          )}
          
          {photo ? (
            isLoading ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography>Loading image...</Typography>
              </Box>
            ) : loadError ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <Typography color="error" sx={{ mb: 2 }}>{loadError}</Typography>
                <Button variant="contained" onClick={handleRetryLoad}>
                  Retry Loading
                </Button>
              </Box>
            ) : image ? (() => {
              const { width: imgW, height: imgH } = getDisplayDimensions(image, stageSize);
              const imgX = (stageSize.width - imgW) / 2;
              const imgY = (stageSize.height - imgH) / 2;
              return (
              <Stage
                ref={stageRef}
                width={stageSize.width}
                height={stageSize.height}
                onMouseDown={handleStageMouseDown}
                onMouseMove={handleStageMouseMove}
                onMouseUp={handleStageMouseUp}
                scaleX={zoomLevel}
                scaleY={zoomLevel}
                x={stagePosition.x}
                y={stagePosition.y}
              >
                <Layer>
                  <KonvaImage
                    ref={imageRef}
                    image={image}
                    width={imgW}
                    height={imgH}
                    x={imgX}
                    y={imgY}
                    onDragStart={handleImageDragStart}
                    listening={mode === MODE.ANNOTATE}
                  />
                  
                  {/* Render existing annotations */}
                  {(() => {
                    // Calculate image bounds for smart label positioning
                    const { width: displayWidth, height: displayHeight } = image ? getDisplayDimensions(image, stageSize) : { width: 0, height: 0 };
                    const imagePositionX = (stageSize.width - displayWidth) / 2;
                    const imagePositionY = (stageSize.height - displayHeight) / 2;
                    const imageBounds = {
                      top: imagePositionY,
                      bottom: imagePositionY + displayHeight,
                      left: imagePositionX,
                      right: imagePositionX + displayWidth
                    };

                    return getVisibleAnnotations().map((annotation) => {
                      const isSelected = selectedAnnotation && selectedAnnotation.id === annotation.id && mode === MODE.ANNOTATE;
                      const boxStyle = getBoxStyle(isSelected, annotation.status);
                      const labelPositions = calculateLabelPositions(annotation, imageBounds);

                      return (
                        <Group key={annotation.id}>
                          {/* Render segmentation polygon(s) if they exist and are visible */}
                        {showSegmentation && annotation.segmentationPolygon && annotation.segmentationPolygon.map((polygon, polygonIndex) => {
                          const pathData = pointsToPath(polygon);
                          
                          // Determine fill color: use random color if enabled, otherwise use default box style color
                          const fillColor = useRandomColors && segmentationColors.has(annotation.id) 
                            ? segmentationColors.get(annotation.id) 
                            : boxStyle.stroke;
                          
                          console.log(`[Enhanced_ImageViewerModal] Rendering polygon ${polygonIndex + 1}/${annotation.segmentationPolygon.length} for annotation ${annotation.id}:`, {
                            pointCount: polygon.length,
                            fillColor: fillColor,
                            useRandomColors: useRandomColors,
                            pathData: pathData.substring(0, 100) + '...' // Truncate for readability
                          });
                          
                          return (
                            <Path
                              key={`${annotation.id}-polygon-${polygonIndex}`}
                              data={pathData}
                              fill={fillColor}
                              opacity={0.3}
                              closed={true}
                              listening={false}
                            />
                          );
                        })}
                        {/* Primary bounding box */}
                        <Rect
                          x={annotation.x}
                          y={annotation.y}
                          width={annotation.width}
                          height={annotation.height}
                          fill="transparent"
                          stroke={boxStyle.stroke}
                          strokeWidth={boxStyle.strokeWidth}
                          dash={boxStyle.dash}
                          onClick={() => handleAnnotationClick(annotation)}
                          onMouseDown={(e) => handleAnnotationDragStart(e, annotation)}
                          listening={mode === MODE.ANNOTATE}
                          // Change cursor to move when hovering over box
                          onMouseOver={(e) => {
                            if (mode === MODE.ANNOTATE) {
                              const container = e.target.getStage().container();
                              container.style.cursor = 'move';
                            }
                          }}
                          onMouseOut={(e) => {
                            const container = e.target.getStage().container();
                            container.style.cursor = 'default';
                          }}
                        />
                        
                        {/* Display confidence score at top-left inside the box */}
                        {annotation.confidenceScore && (
                          <Text
                            x={annotation.x + 5}
                            y={annotation.y + 5}
                            text={`${Math.round(annotation.confidenceScore * 100)}%`}
                            fontSize={12}
                            fill="white"
                            fontStyle="bold"
                            shadowColor="black"
                            shadowBlur={5}
                            shadowOpacity={0.9}
                            listening={false}
                          />
                        )}
                        
                        {/* Pencil icon in top left when in annotation mode */}
                        {mode === MODE.ANNOTATE && (
                          <Group>
                            <Circle
                              x={annotation.x + 12}
                              y={annotation.y + 12}
                              radius={10}
                              fill="#3f51b5"
                              opacity={0.9}
                              stroke="#ffffff"
                              strokeWidth={1}
                            />
                            {/* Custom SVG path for edit icon, similar to MUI EditIcon */}
                            <Path
                              x={annotation.x + 5}
                              y={annotation.y + 5}
                              data="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                              scale={{ x: 0.6, y: 0.6 }}
                              fill="white"
                              listening={false}
                            />
                          </Group>
                        )}
                        
                        {/* Display status label - smart positioned */}
                        <Group>
                          <Rect
                            x={annotation.x + annotation.width - 115}
                            y={labelPositions.statusY}
                            width={115}
                            height={18}
                            fill={STATUS_COLORS[annotation.status] || '#9E9E9E'}
                            cornerRadius={6}
                            opacity={0.9}
                            onClick={(e) => !viewOnlyMode && annotation.defectTypeId ? handleStatusMenuOpen(e.evt, annotation) : null}
                            onTap={(e) => !viewOnlyMode && annotation.defectTypeId ? handleStatusMenuOpen(e.evt, annotation) : null}
                            listening={!viewOnlyMode && !!annotation.defectTypeId}
                            cursor={!viewOnlyMode && annotation.defectTypeId ? "pointer" : "default"}
                            shadowColor="rgba(0,0,0,0.2)"
                            shadowBlur={3}
                            shadowOffset={{x: 1, y: 1}}
                          />
                          <Text
                            x={annotation.x + annotation.width - 105}
                            y={labelPositions.statusY + 6}
                            text={
                              // Show (AI) or (manual) based on isAiGenerated for draft status
                              annotation.status === 'draft'
                                ? (annotation.isAiGenerated ? 'draft (AI)' : 'draft (manual)')
                                : annotation.status // For other statuses like 'validated', 'rejected'
                            }
                            fontSize={11}
                            fontFamily="Arial"
                            fill="white"
                            fontStyle="bold"
                            onClick={(e) => !viewOnlyMode && annotation.defectTypeId ? handleStatusMenuOpen(e.evt, annotation) : null}
                            onTap={(e) => !viewOnlyMode && annotation.defectTypeId ? handleStatusMenuOpen(e.evt, annotation) : null}
                            listening={!viewOnlyMode && !!annotation.defectTypeId}
                            cursor={!viewOnlyMode && annotation.defectTypeId ? "pointer" : "default"}
                          />
                          {/* Add dropdown icon only if defect type is selected */}
                          {!viewOnlyMode && annotation.defectTypeId && (
                            <>
                              <Rect
                                x={annotation.x + annotation.width - 15}
                                y={annotation.y - 18}
                                width={13}
                                height={16}
                                cornerRadius={[0, 3, 3, 0]}
                                fill="rgba(255, 255, 255, 0.2)"
                                onClick={(e) => handleStatusMenuOpen(e.evt, annotation)}
                                onTap={(e) => handleStatusMenuOpen(e.evt, annotation)}
                                listening={true}
                                cursor="pointer"
                              />
                              <Text
                                x={annotation.x + annotation.width - 13}
                                y={annotation.y - 15}
                                text="⌄"
                                fontSize={12}
                                fontFamily="Arial"
                                fill="white"
                                onClick={(e) => handleStatusMenuOpen(e.evt, annotation)}
                                onTap={(e) => handleStatusMenuOpen(e.evt, annotation)}
                                listening={true}
                                cursor="pointer"
                              />
                            </>
                          )}
                        </Group>
                        
                        {/* Render anchor points for resizing if selected */}
                        {isSelected && ANCHORS.map((anchor, index) => {
                          const anchorX = annotation.x + annotation.width * anchor.x;
                          const anchorY = annotation.y + annotation.height * anchor.y;
                          
                          return (
                            <Circle
                              key={index}
                              x={anchorX}
                              y={anchorY}
                              radius={6}
                              fill="yellow"
                              stroke="orange"
                              strokeWidth={1}
                              onMouseDown={(e) => handleAnchorMouseDown(e, index)}
                              onMouseOver={(e) => {
                                if (mode === MODE.ANNOTATE) {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = anchor.cursor;
                                }
                              }}
                              onMouseOut={(e) => {
                                const container = e.target.getStage().container();
                                container.style.cursor = 'default';
                              }}
                            />
                          );
                        })}
                        
                        {/* Replace the separate defect type and severity labels with a single combined label - smart positioned */}
                        <Group>
                          <Rect
                            x={annotation.x}
                            y={labelPositions.defectY}
                            width={!annotation.defectTypeId ? 120 :
                                 !annotation.severityLevelId ? Math.max(180, annotation.defectType.length * 8 + 95) :
                                 Math.max(120, (annotation.defectType.length + annotation.severityLevel?.length) * 7 + 30)}
                            height={18}
                            fill={!annotation.defectTypeId ? '#FF5722' : (annotation.severityLevelId ? 'rgba(63, 81, 181, 0.8)' : 'linear-gradient(90deg, #3f51b5 50%, #FF9800 50%)')}
                            cornerRadius={6}
                            opacity={0.9}
                            listening={annotation.status !== 'validated'}
                            cursor={annotation.status !== 'validated' ? 'pointer' : 'default'}
                            shadowColor="rgba(0,0,0,0.2)"
                            shadowBlur={3}
                            shadowOffset={{x: 1, y: 1}}
                          />

                          {!annotation.defectTypeId ? (
                            // Case 1: No defect type selected yet - show prompt to select defect type
                            <Text
                              x={annotation.x + 5}
                              y={labelPositions.defectY + 6}
                              text="Select defect type"
                              fontSize={11}
                              fontFamily="Arial"
                              fill="white"
                              fontStyle="bold"
                              width={100}
                              ellipsis={true}
                              onClick={!viewOnlyMode && annotation.status !== 'validated' ? (e) => handleDefectTypeMenuOpen(e.evt, annotation) : undefined}
                              onTap={!viewOnlyMode && annotation.status !== 'validated' ? (e) => handleDefectTypeMenuOpen(e.evt, annotation) : undefined}
                              listening={!viewOnlyMode && annotation.status !== 'validated'}
                              cursor={!viewOnlyMode && annotation.status !== 'validated' ? 'pointer' : 'default'}
                            />
                          ) : !annotation.severityLevelId ? (
                            // Case 2: Defect type selected, no severity - show defect type and prompt for severity
                            <>
                              {/* Defect type part */}
                              <Text
                                x={annotation.x + 5}
                                y={labelPositions.defectY + 6}
                                text={annotation.defectType}
                                fontSize={11}
                                fontFamily="Arial"
                                fill="white"
                                fontStyle="bold"
                                width={Math.max(60, annotation.defectType.length * 6)}
                                ellipsis={true}
                                onClick={!viewOnlyMode && annotation.status !== 'validated' ? (e) => handleDefectTypeMenuOpen(e.evt, annotation) : undefined}
                                onTap={!viewOnlyMode && annotation.status !== 'validated' ? (e) => handleDefectTypeMenuOpen(e.evt, annotation) : undefined}
                                listening={!viewOnlyMode && annotation.status !== 'validated'}
                                cursor={!viewOnlyMode && annotation.status !== 'validated' ? 'pointer' : 'default'}
                              />

                              {/* Divider */}
                              <Rect
                                x={annotation.x + Math.max(annotation.defectType.length * 6, 60) + 10}
                                y={labelPositions.defectY + 2}
                                width={2}
                                height={14}
                                fill="rgba(255, 255, 255, 0.5)"
                                listening={false}
                              />

                              {/* Severity part - prompt */}
                              <Text
                                x={annotation.x + Math.max(annotation.defectType.length * 6, 60) + 17}
                                y={labelPositions.defectY + 6}
                                text="Defect Rating"
                                fontSize={11}
                                fontFamily="Arial"
                                fill="white"
                                fontStyle="bold"
                                width={95}
                                ellipsis={true}
                                onClick={!viewOnlyMode ? (e) => handleSeverityMenuOpen(e.evt, annotation) : undefined}
                                onTap={!viewOnlyMode ? (e) => handleSeverityMenuOpen(e.evt, annotation) : undefined}
                                listening={!viewOnlyMode && annotation.status !== 'validated'}
                                cursor={!viewOnlyMode && annotation.status !== 'validated' ? 'pointer' : 'default'}
                              />
                            </>
                          ) : (
                            // Case 3: Both defect type and severity selected - show combined label
                            <>
                              <Text
                                x={annotation.x + 5}
                                y={labelPositions.defectY + 6}
                                text={`${annotation.defectType} • ${annotation.severityLevel}`}
                                fontSize={11}
                                fontFamily="Arial"
                                fill="white"
                                fontStyle="bold"
                                width={Math.max(100, (annotation.defectType.length + annotation.severityLevel?.length) * 7 + 15)}
                                ellipsis={true}
                                listening={false}
                              />

                              {/* Calculate dividing point based on text length */}
                              {(() => {
                                const totalLength = Math.max(120, (annotation.defectType.length + annotation.severityLevel?.length) * 7 + 30);
                                const dividerPos = Math.max(annotation.defectType.length * 6, 60) + 5;
                                return (
                                  <>
                                    {/* Invisible hit areas for clicking different parts */}
                                    <Rect
                                      x={annotation.x}
                                      y={labelPositions.defectY}
                                      width={dividerPos}
                                      height={18}
                                      fill="transparent"
                                      onClick={!viewOnlyMode && annotation.status !== 'validated' ? (e) => handleDefectTypeMenuOpen(e.evt, annotation) : undefined}
                                      onTap={!viewOnlyMode && annotation.status !== 'validated' ? (e) => handleDefectTypeMenuOpen(e.evt, annotation) : undefined}
                                      listening={!viewOnlyMode && annotation.status !== 'validated'}
                                      cursor={!viewOnlyMode && annotation.status !== 'validated' ? 'pointer' : 'default'}
                                    />

                                    <Rect
                                      x={annotation.x + dividerPos}
                                      y={labelPositions.defectY}
                                      width={totalLength - dividerPos}
                                      height={18}
                                      fill="transparent"
                                      onClick={!viewOnlyMode ? (e) => handleSeverityMenuOpen(e.evt, annotation) : undefined}
                                      onTap={!viewOnlyMode ? (e) => handleSeverityMenuOpen(e.evt, annotation) : undefined}
                                      listening={!viewOnlyMode && annotation.status !== 'validated'}
                                      cursor={!viewOnlyMode && annotation.status !== 'validated' ? 'pointer' : 'default'}
                                    />
                                  </>
                                );
                              })()}
                            </>
                          )}

                          {/* Dropdown icon */}
                          {!viewOnlyMode && annotation.status !== 'validated' && (
                            <>
                              <Rect
                                x={annotation.x + (!annotation.defectTypeId ? 105 :
                                               !annotation.severityLevelId ? Math.max(165, annotation.defectType.length * 6 + 95) :
                                               Math.max(105, (annotation.defectType.length + annotation.severityLevel?.length) * 7 + 15))}
                                y={labelPositions.defectY}
                                width={13}
                                height={18}
                                cornerRadius={[0, 3, 3, 0]}
                                fill="rgba(255, 255, 255, 0.2)"
                                onClick={(e) => !annotation.defectTypeId ? handleDefectTypeMenuOpen(e.evt, annotation) : 
                                             !annotation.severityLevelId ? handleSeverityMenuOpen(e.evt, annotation) :
                                             handleDefectTypeMenuOpen(e.evt, annotation)}
                                onTap={(e) => !annotation.defectTypeId ? handleDefectTypeMenuOpen(e.evt, annotation) : 
                                           !annotation.severityLevelId ? handleSeverityMenuOpen(e.evt, annotation) :
                                           handleDefectTypeMenuOpen(e.evt, annotation)}
                                listening={true}
                                cursor="pointer"
                              />
                              <Text
                                x={annotation.x + (!annotation.defectTypeId ? 107 : 
                                               !annotation.severityLevelId ? Math.max(167, annotation.defectType.length * 6 + 97) :
                                               Math.max(107, (annotation.defectType.length + annotation.severityLevel?.length) * 7 + 17))}
                                y={annotation.y + annotation.height + 5}
                                text="⌄"
                                fontSize={12}
                                fontFamily="Arial"
                                fill="white"
                                onClick={(e) => !annotation.defectTypeId ? handleDefectTypeMenuOpen(e.evt, annotation) : 
                                             !annotation.severityLevelId ? handleSeverityMenuOpen(e.evt, annotation) :
                                             handleDefectTypeMenuOpen(e.evt, annotation)}
                                onTap={(e) => !annotation.defectTypeId ? handleDefectTypeMenuOpen(e.evt, annotation) : 
                                           !annotation.severityLevelId ? handleSeverityMenuOpen(e.evt, annotation) :
                                           handleDefectTypeMenuOpen(e.evt, annotation)}
                                listening={true}
                                cursor="pointer"
                              />
                            </>
                          )}
                        </Group>
                      </Group>
                    );
                  });
                  })()}

                  {/* Render current annotation being drawn */}
                  {currentAnnotation && isDrawing && (
                    <Rect
                      stroke={BOX_STYLE.ANNOTATE.DRAWING.stroke}
                      strokeWidth={BOX_STYLE.ANNOTATE.DRAWING.strokeWidth}
                      dash={BOX_STYLE.ANNOTATE.DRAWING.dash}
                      x={currentAnnotation.x}
                      y={currentAnnotation.y}
                      width={currentAnnotation.width}
                      height={currentAnnotation.height}
                      fill="transparent"
                    />
                  )}
                </Layer>
              </Stage>
              );
            })() : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <Typography color="error">Failed to load image.</Typography>
                <Button variant="contained" onClick={handleRetryLoad} sx={{ mt: 2 }}>
                  Retry Loading
                </Button>
              </Box>
            )
          ) : (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
              <Typography variant="body1" color="text.secondary">
                No photo selected.
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Add Annotation Coordinates Table */}
          {annotations.length > 0 && image && (
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: '250px', overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Defect Type</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getVisibleAnnotations().map((annotation, index) => {
                    const isZoomedToThis = isZoomed && selectedAnnotation && selectedAnnotation.id === annotation.id;
                    
                    return (
                      <TableRow
                        key={annotation.id}
                        sx={{
                          backgroundColor: selectedAnnotation && selectedAnnotation.id === annotation.id
                            ? 'rgba(255, 215, 0, 0.1)'
                            : 'inherit',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            cursor: 'pointer'
                          }
                        }}
                        onClick={() => handleAnnotationClick(annotation)}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {
                            annotation.originalX1 === undefined
                              ? <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>New</Typography>
                              : (annotation.status === 'validated' && annotation.defect_id)
                                ? <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 'bold', 
                                      color: 'success.main',
                                      fontFamily: 'monospace'
                                    }}
                                  >
                                    D-{annotation.defect_id}
                                  </Typography>
                                : <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: 'text.secondary',
                                      fontFamily: 'monospace'
                                    }}
                                  >
                                    {annotation.id}
                                  </Typography>
                          }
                        </TableCell>
                        <TableCell
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!viewOnlyMode && annotation.status !== 'validated') {
                              handleDefectTypeMenuOpen(e, annotation);
                            }
                          }}
                          sx={{
                            cursor: !viewOnlyMode && annotation.status !== 'validated' ? 'pointer' : 'default',
                            '&:hover': !viewOnlyMode && annotation.status !== 'validated' ? { bgcolor: 'action.hover' } : {},
                            color: !annotation.defectTypeId ? 'error.main' : 'inherit',
                            fontWeight: !annotation.defectTypeId ? 'bold' : 'normal'
                          }}
                        >
                          {annotation.defectType || 'Select type ▼'}
                        </TableCell>
                        <TableCell
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!viewOnlyMode && annotation.status !== 'validated' && annotation.defectTypeId) {
                              handleSeverityMenuOpen(e, annotation);
                            }
                          }}
                          sx={{
                            cursor: !viewOnlyMode && annotation.status !== 'validated' && annotation.defectTypeId ? 'pointer' : 'default',
                            '&:hover': !viewOnlyMode && annotation.status !== 'validated' && annotation.defectTypeId ? { bgcolor: 'action.hover' } : {},
                            color: annotation.defectTypeId && !annotation.severityLevelId ? 'warning.main' : 'inherit',
                            fontWeight: annotation.defectTypeId && !annotation.severityLevelId ? 'bold' : 'normal'
                          }}
                        >
                          {annotation.severityLevel || (annotation.defectTypeId ? 'Select rating ▼' : 'N/A')}
                        </TableCell>
                        <TableCell>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!viewOnlyMode && annotation.status !== 'validated' && annotation.defectTypeId) {
                                handleStatusMenuOpen(e, annotation);
                              }
                            }}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              bgcolor: STATUS_COLORS[annotation.status] || '#9E9E9E',
                              color: 'white',
                              px: 1,
                              py: 0.2,
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              cursor: !viewOnlyMode && annotation.status !== 'validated' && annotation.defectTypeId ? 'pointer' : 'default',
                              '&:hover': !viewOnlyMode && annotation.status !== 'validated' && annotation.defectTypeId ? { opacity: 0.8 } : {}
                            }}
                          >
                            {annotation.status ? annotation.status.charAt(0).toUpperCase() + annotation.status.slice(1) : 'N/A'}
                            {!viewOnlyMode && annotation.status !== 'validated' && annotation.defectTypeId && ' ▼'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 'bold',
                              color: annotation.isAiGenerated ? 'primary.main' : 'text.secondary'
                            }}
                          >
                            {annotation.isAiGenerated ? 'AI' : 'Manual'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={annotation.notes || 'No notes'} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                maxWidth: 120,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                color: annotation.notes ? 'text.primary' : 'text.disabled',
                                fontStyle: annotation.notes ? 'normal' : 'italic'
                              }}
                            >
                              {annotation.notes || 'No notes'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {
                            (() => {
                              let displayString = '-';
                              // If AI generated and updated by system, show "AI Analysis"
                              if (annotation.isAiGenerated && (annotation.updated_by === 'system' || !annotation.updated_by_full_name)) {
                                displayString = 'AI Analysis';
                                if (annotation.updated_at) {
                                  displayString += ` at ${new Date(annotation.updated_at).toLocaleString()}`;
                                }
                                return displayString;
                              }

                              // Otherwise, show user info
                              const userPart = annotation.updated_by_full_name || annotation.updated_by_email || annotation.updated_by?.toString();

                              if (userPart) {
                                displayString = userPart;
                                if (annotation.updated_at) {
                                  displayString += ` at ${new Date(annotation.updated_at).toLocaleString()}`;
                                }
                              } else if (annotation.updated_at) {
                                displayString = `Updated at ${new Date(annotation.updated_at).toLocaleString()}`;
                              }
                              return displayString;
                            })()
                          }
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Edit Notes">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  handleEditNotes(annotation);
                                }}
                                disabled={annotation.originalX1 === undefined} // Disable for unsaved annotations
                              >
                                <EditIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Focus on This Annotation">
                              <IconButton
                                size="small"
                                color={focusMode === FOCUS_MODE.SELECTED && selectedAnnotation?.id === annotation.id ? "secondary" : "default"}
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  focusOnAnnotation(annotation);
                                }}
                              >
                                <CenterFocusStrongIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isZoomedToThis ? "Reset Zoom" : "Zoom to Annotation"}>
                              <IconButton
                                size="small"
                                color={isZoomedToThis ? "secondary" : "primary"}
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  handleZoomToAnnotation(annotation);
                                }}
                              >
                                {isZoomedToThis ? <ZoomOutIcon /> : <ZoomInIcon />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        
        {mode === MODE.ANNOTATE && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', color: 'white', borderRadius: 1 }}>
            <Typography variant="body2">
              <b>Annotation Mode Active:</b> Click and drag to create a new box. Click a box to select it.
              Drag a box to reposition it, or use the anchor points to resize it.
              Press <b>F</b> to toggle focus mode and hide other annotations. Press <b>Tab</b> to cycle through annotations in focus mode.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
          <Button onClick={handleSafeClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </ContentWrapper>

      {/* Add Confirmation Dialog for Clear All */}
      <Dialog 
        open={clearAllConfirmOpen} 
        onClose={closeClearAllConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            bgcolor: '#ffebee', 
            width: 40, 
            height: 40, 
            borderRadius: '50%' 
          }}>
            <DeleteIcon color="error" />
          </Box>
          <Typography variant="h6">Confirm Deletion</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to permanently delete all annotations for this image
            {photo?.filename ? ` "${photo.filename}"` : ''}?
          </Typography>
          
          <Box sx={{ 
            bgcolor: '#ffebee', 
            p: 2, 
            borderRadius: 1, 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            mb: 2
          }}>
            <WarningIcon color="error" fontSize="small" />
            <Typography variant="body2" color="error.main">
              This will delete {annotations.filter(ann => !isNaN(parseInt(ann.id))).length} annotation(s) from the database.
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={closeClearAllConfirm}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={confirmClearAll}
            startIcon={<DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            Delete Annotations
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Snackbar for API feedback */}
      <Snackbar 
        open={apiStatus.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={apiStatus.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {apiStatus.message}
        </Alert>
      </Snackbar>
      
      {/* Status Change Menu */}
      <Menu
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={statusMenuAnchor}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        {statusOptions.map((status) => (
          <MenuItem 
            key={status} 
            onClick={() => handleStatusChange(status)}
            disabled={selectedStatusAnnotation?.status === status}
            sx={{ 
              fontSize: '13px',
              padding: '8px 16px',
              backgroundColor: selectedStatusAnnotation?.status === status ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)'
              },
              '&::before': {
                content: '""',
                display: 'block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: STATUS_COLORS[status] || '#9E9E9E',
                marginRight: 1
              }
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </MenuItem>
        ))}
      </Menu>

      {/* Defect Type Change Menu */}
      <Menu
        open={Boolean(defectTypeMenuAnchor)}
        onClose={handleDefectTypeMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={defectTypeMenuAnchor}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            overflowY: 'auto', // Keep auto scrolling capability for extreme cases
            // maxHeight removed to show all items without scrolling
          }
        }}
      >
        {defectTypes.length > 0 ? (
          defectTypes.map((defectType) => (
            <MenuItem 
              key={defectType.id} 
              onClick={() => handleDefectTypeChange(defectType.id, defectType.name)}
              disabled={selectedDefectTypeAnnotation?.defectTypeId === defectType.id}
              sx={{ 
                fontSize: '13px',
                padding: '8px 16px',
                backgroundColor: selectedDefectTypeAnnotation?.defectTypeId === defectType.id ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)'
                },
                '&::before': {
                  content: '""',
                  display: 'block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: DEFECT_TYPE_COLOR,
                  marginRight: 1
                }
              }}
            >
              {defectType.name.charAt(0).toUpperCase() + defectType.name.slice(1)}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled sx={{ fontSize: '13px', padding: '8px 16px', color: 'text.secondary' }}>
            No defect types defined for this asset.
          </MenuItem>
        )}
      </Menu>

      {/* Severity Level Menu */}
      <Menu
        open={Boolean(severityMenuAnchor)}
        onClose={handleSeverityMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={severityMenuAnchor}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            overflowY: 'auto',
          }
        }}
      >
        {severityLevels.length > 0 ? (
          severityLevels.map((level) => (
            <Tooltip title={level.description || ''} placement="right" key={level.id}>
              <MenuItem
                onClick={() => handleSeverityChange(level.id, level.name)}
                disabled={selectedSeverityAnnotation?.severityLevelId === level.id}
                sx={{
                  fontSize: '13px',
                  padding: '8px 16px',
                  backgroundColor: selectedSeverityAnnotation?.severityLevelId === level.id ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)'
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: level.color_code || '#FF9800',
                    marginRight: 1
                  }
                }}
              >
                {level.name}
              </MenuItem>
            </Tooltip>
          ))
        ) : (
          <MenuItem disabled sx={{ fontSize: '13px', padding: '8px 16px', color: 'text.secondary' }}>
            No severity levels defined for this asset.
          </MenuItem>
        )}
      </Menu>

      {/* Status Notes Dialog */}
      <Dialog
        open={statusNotesDialogOpen}
        onClose={handleCancelStatusChange}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 35px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            border: `1px solid ${pendingStatusChange?.newStatus === 'validated' ? 'success.main' : 'error.main'}`
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          pb: 2,
          bgcolor: pendingStatusChange?.newStatus === 'validated' ? 'success.light' : 'error.light',
          color: pendingStatusChange?.newStatus === 'validated' ? 'success.contrastText' : 'error.contrastText'
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: pendingStatusChange?.newStatus === 'validated' ? 'success.main' : 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}>
            {pendingStatusChange?.newStatus === 'validated' ? '✓' : '✕'}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {pendingStatusChange?.newStatus === 'validated' ? 'Validate Annotation' : 'Reject Annotation'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Add optional remarks for this {pendingStatusChange?.newStatus} action:
          </Typography>
          
          <TextField
            label="Remarks (Optional)"
            multiline
            rows={4}
            fullWidth
            value={statusRemarks}
            onChange={(e) => setStatusRemarks(e.target.value)}
            placeholder={
              pendingStatusChange?.newStatus === 'validated' 
                ? "e.g., Confirmed defect severity, matches field observations..."
                : "e.g., False positive due to lighting conditions, not a defect..."
            }
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
          
          <Typography variant="caption" sx={{ 
            display: 'block', 
            mt: 1, 
            color: 'text.disabled',
            fontStyle: 'italic'
          }}>
            Notes will be saved with the annotation and copied to the defect record when validated.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={handleCancelStatusChange}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              borderColor: 'divider'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatusChange}
            variant="contained"
            color={pendingStatusChange?.newStatus === 'validated' ? 'success' : 'error'}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120
            }}
          >
            {pendingStatusChange?.newStatus === 'validated' ? 'Validate' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Editing Dialog */}
      <Dialog
        open={notesEditDialogOpen}
        onClose={handleCancelNotesEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 35px rgba(0,0,0,0.18)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          pb: 2,
          bgcolor: 'primary.light',
          color: 'primary.contrastText'
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}>
            <EditIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Annotation Notes
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Update notes for annotation #{editingNotesAnnotation?.id}:
          </Typography>
          
          <TextField
            label="Notes"
            multiline
            rows={5}
            fullWidth
            value={editingNotes}
            onChange={(e) => setEditingNotes(e.target.value)}
            placeholder="Add detailed notes about this annotation..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
          
          <Typography variant="caption" sx={{ 
            display: 'block', 
            mt: 1, 
            color: 'text.disabled',
            fontStyle: 'italic'
          }}>
            These notes will be preserved when the annotation is validated and transferred to the defect record.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={handleCancelNotesEdit}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              borderColor: 'divider'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNotesEdit}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100
            }}
          >
            Save Notes
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default Enhanced_ImageViewerModal;